import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense, lazy } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Edit2, Trash2, MoreVertical, Phone, Mail, UserCircle, Loader2, Download, Search, Eye, Upload, UserPlus, FileText as FileTextIcon, Calendar as CalendarIcon, Scissors, SlidersHorizontal, X, Users, Layers, BookOpen, ChevronDown, Rows3, Rows2, Sparkles, CopyCheck, PauseCircle, UserCheck } from 'lucide-react';
import ActionMenu from '../components/ActionMenu';
import Pagination from '../components/Table/Pagination';
import KPICard from '../components/Dashboard/KPICard';
import Table from '../components/Table';
import api from '../services/api';
import { useYear } from '../context/YearContext';
import { useToastContext } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';
import Breadcrumb from '../components/Breadcrumb';
import ConfirmationModal from '../components/ConfirmationModal';
import StudentImportModal from '../components/StudentImportModal';
import DuplicateDetectionModal from '../components/DuplicateDetectionModal';
import EnterpriseModal from '../components/EnterpriseModal';
import GroupPicker from '../components/Groups/GroupPicker';
import { generateTemplateCSV, downloadCSV as downloadCSVFile, buildStudentsExportCSV } from '../utils/csvUtils';
import { saveFile } from '../utils/saveFile';
import { openExternal } from '../utils/openExternal';
import { formatDateOnly } from '../utils/localeFormatters';
import { useSmartTranslation, useQuranCenter, useSportCenter, useLearningCenter } from '../hooks/useStudentLabel';
import quranApi from '../api/quran';
import GoalProgressBadge from '../components/Quran/GoalProgressBadge';
import {
  hasGoal as quranHasGoal,
  isBehindPlan as quranIsBehind,
  planState as quranPlanState,
  goalPercent as quranGoalPercent,
  hasSurahStatus as quranHasSurahStatus,
  MASTERY_STATUSES,
  STATUS_KEY_SUFFIX,
} from '../utils/quranStatus';
import { useDebounceWithLoading } from '../hooks/useDebounce';
import { EmptyStates } from '../components/EmptyState';
import useAuthStore from '../store/authStore';
import ExportPdfButton from '../components/ExportPdfButton';
import WhatsAppButton from '../components/WhatsAppButton';
import { studentFullName, studentInitials } from '../utils/studentName';

// Hair-transplant only, and only once a client is picked — keep it out of
// the main bundle every center loads.
const ScheduleTreatmentModal = lazy(() => import('../components/TransplantCrm/ScheduleTreatmentModal'));

// Deterministic avatar color from a name. Same name → same hue across sessions,
// so admins build visual recognition for repeat clients (Notion / Linear pattern).
// Returns soft pastel bg + readable foreground.
function avatarColorFromName(name) {
  const str = String(name || '').trim() || '?';
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) | 0;
  const hue = Math.abs(hash) % 360;
  return {
    bg: `hsl(${hue}, 65%, 92%)`,
    fg: `hsl(${hue}, 55%, 32%)`,
    border: `hsl(${hue}, 50%, 80%)`,
  };
}

// Outlined header button (CSV export / import) sitting next to the primary Add.
const headerSecondaryBtnStyle = (isMobile, busy) => ({
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text-primary)',
  padding: isMobile ? '0.375rem 0.5rem' : '0.5rem 0.875rem',
  borderRadius: '0.5rem',
  cursor: busy ? 'wait' : 'pointer',
  opacity: busy ? 0.7 : 1,
  fontWeight: '500',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.375rem',
  whiteSpace: 'nowrap',
  fontSize: isMobile ? '0.75rem' : '0.875rem',
});

// Density preset persisted to localStorage so power users keep their choice.
const DENSITY_KEY = 'studentsPage.density';
const readDensity = () => {
  try {
    const v = localStorage.getItem(DENSITY_KEY);
    return v === 'compact' || v === 'comfortable' ? v : 'comfortable';
  } catch { return 'comfortable'; }
};

export default function StudentsPage() {
  const { i18n } = useTranslation();
  const t = useSmartTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  const navigate = useNavigate();
  const { selectedYear, setSelectedYear, years, yearResolved } = useYear();
  const { success, error, withUndo } = useToastContext();
  const confirm = useConfirm();
  const centerType = useAuthStore((state) => state.centerType);
  const { isQuran } = useQuranCenter();
  // Sport clients have no school grade: hide the grade chips, filter and column.
  const { isSport } = useSportCenter();
  const isHairTransplant = centerType === 'hair_transplant';
  const isTherapyCenter = centerType === 'therapy';
  const isClinicCenter = isTherapyCenter || isHairTransplant; // non-learning centers
  // Hair-transplant clients live in a separate `transplant_clients` Firestore
  // collection and are fetched via `/api/transplant-clients`. Therapy and
  // learning centers both use the `/api/students` endpoint. The rest of this
  // page treats the two datasets uniformly after a small normalization step
  // in the fetch effect below (fullName → firstName/lastName).
  const listEndpoint = isHairTransplant ? '/transplant-clients' : '/students';

  // Canonical path to the per-center detail page. URL prefix follows the
  // entity's center type purely for cosmetic address-bar correctness; both
  // aliases mount `StudentDetailRouter` which picks the right page.
  const studentDetailPath = React.useCallback(
    (id) => `${centerType === 'learning' ? '/students' : '/clients'}/${id}`,
    [centerType]
  );

  // Search with debounce. ?search= pre-fills it (links from the ChatGPT
  // connector open a search here instead of naming students in the chat).
  const location = useLocation();
  const [search, setSearch] = useState(() => new URLSearchParams(location.search).get('search')?.slice(0, 100) || '');
  const { debouncedValue: debouncedSearch, isDebouncing: isSearching } = useDebounceWithLoading(search, 300);

  // Advanced filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(
    isClinicCenter
      ? { phone: '', status: '', surveyStatus: '', notes: '', dobFrom: '', dobTo: '' }
      : { phone: '', class: '', grade: '', subjectId: '', groupId: '', notes: '' }
  );
  const [subjects, setSubjects] = useState([]);
  const [groupsList, setGroupsList] = useState([]);

  // Sport active / inactive (docs/habib-sports/OWNER-REQUESTS-2026-10-21-SPEC.md
  // §C): '' (all) | 'active' | 'inactive', with the server's chip counts.
  const userRole = useAuthStore((state) => state.user?.role);
  // Every learning center has it since 2026-10-22 (sport first); clinics keep
  // their own status values.
  const { isLearning } = useLearningCenter();
  const hasClientStatus = isLearning && !isClinicCenter;
  const canManageStatus = hasClientStatus && ['admin', 'manager', 'center_manager'].includes(userRole);
  const inactiveHintKey = isSport ? 'sport.clientStatus.inactiveHint' : 'students.clientStatus.inactiveHint';
  const [sportStatus, setSportStatus] = useState('');
  const [statusCounts, setStatusCounts] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);

  // Therapy KPI stats
  const [therapyStats, setTherapyStats] = useState({ totalActive: 0, pendingSurveys: 0, newThisMonth: 0 });
  const [surveyDataMap, setSurveyDataMap] = useState({});

  // Quran (Hifz) per-student goal status (Quran centers only). Keyed by
  // studentId → studentStatus payload (or null). `quranFilters` drives the
  // memorization-status column filters; all client-side over the current page.
  const [quranStatusMap, setQuranStatusMap] = useState({});
  const [quranFilters, setQuranFilters] = useState({ status: '', behind: false, noGoal: false });

  // Birthday-gift data-quality banner: clients with missing/other gender or missing DOB
  const [birthdayIssueCount, setBirthdayIssueCount] = useState(0);
  const [birthdayBannerDismissed, setBirthdayBannerDismissed] = useState(() => {
    try { return sessionStorage.getItem('birthdayBannerDismissed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    let cancelled = false;
    api.get('/birthday-gifts/clients-with-issues')
      .then((res) => {
        // api.get has already unwrapped { success, data } to the array.
        const list = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
        if (!cancelled) setBirthdayIssueCount(list.length);
      })
      .catch(() => { /* non-blocking */ });
    return () => { cancelled = true; };
  }, []);

  // Loading and data states
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 25,
    totalItems: 0,
    totalPages: 0
  });

  // Sorting state
  const [sorting, setSorting] = useState({
    column: null,
    direction: null
  });

  // Refetch trigger - increment to force data refresh
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Row selection state
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [scheduleClient, setScheduleClient] = useState(null); // client to schedule treatment for
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [formData, setFormData] = useState({
    nationalId: '',
    autoGenerateId: false,
    firstName: '',
    middleName: '',
    lastName: '',
    gender: '',
    dateOfBirth: '',
    phone: '',
    email: '',
    city: '',
    address: '',
    guardianName: '',
    guardianRelationship: '',
    guardianMobile: '',
    guardianId: '',
    parentPhone: '' // Keep for backward compatibility
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [students, setStudents] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [selectedChecklists, setSelectedChecklists] = useState([]);
  // Group the new student joins on save (Section 0 of the add form).
  const [newStudentGroupId, setNewStudentGroupId] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, right: 'auto' });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);
  const menuButtonRefs = useRef({});

  // Density preset for the data table (compact / comfortable). Persisted.
  const [density, setDensity] = useState(readDensity);
  useEffect(() => { try { localStorage.setItem(DENSITY_KEY, density); } catch { /* noop */ } }, [density]);

  // Add Student modal: progressive disclosure for optional sections.
  const [showOptionalSections, setShowOptionalSections] = useState(false);

  // Ref so keyboard shortcut `/` can focus the search input.
  const searchInputRef = useRef(null);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w <= 768);
      setIsTablet(w > 768 && w <= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keyboard shortcuts (learning center): `n` opens Add modal, `/` focuses search.
  // Skip when user is typing in an input/textarea/contenteditable.
  useEffect(() => {
    if (centerType !== 'learning') return;
    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
    };
    const onKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(document.activeElement)) return;
      if (showModal || showImportModal || showDeleteConfirm) return;
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        setShowModal(true);
      } else if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [centerType, showModal, showImportModal, showDeleteConfirm]);

  // Fetch subjects and groups for filters
  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const [subjectsRes, groupsRes] = await Promise.all([
          api.get('/settings/subjects'),
          api.get('/groups')
        ]);
        setSubjects(Array.isArray(subjectsRes) ? subjectsRes : (subjectsRes.data || []));
        setGroupsList(Array.isArray(groupsRes) ? groupsRes : (groupsRes.data || []));
      } catch (err) {
        console.error('Error loading filter data:', err);
      }
    };
    loadFilterData();
  }, []);

  // Fetch therapy-specific stats
  useEffect(() => {
    if (!isTherapyCenter || !yearResolved) return;
    const loadTherapyStats = async () => {
      try {
        // We'll derive stats from loaded data; pending surveys need a separate call
        // For now, totalActive comes from pagination.totalItems
      } catch (err) {
        console.error('Error loading therapy stats:', err);
      }
    };
    loadTherapyStats();
  }, [isTherapyCenter, yearResolved]);

  // Reset progressive-disclosure state whenever the Add modal opens.
  // For clinic centers we also auto-enable national-ID auto-generation so the
  // modal lets the admin save without unhiding the optional Identification
  // section. Most therapy intakes capture the ID later, if at all.
  useEffect(() => {
    if (showModal) {
      setShowOptionalSections(false);
      if (isClinicCenter) {
        setFormData(prev => ({ ...prev, autoGenerateId: true }));
      }
    }
  }, [showModal, isClinicCenter]);

  // Live age preview shown beside the DOB input so admins can sanity-check
  // the date they just typed.
  const computedAge = useMemo(() => {
    if (!formData.dateOfBirth) return null;
    const dob = new Date(formData.dateOfBirth);
    if (Number.isNaN(dob.getTime())) return null;
    const today = new Date();
    let years = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) years--;
    return years >= 0 && years < 150 ? years : null;
  }, [formData.dateOfBirth]);

  // Progress bar fuel: required + recommended fields. Required fields count
  // double so the bar reflects "ready to submit?" rather than "all fields
  // filled?".
  const formProgress = useMemo(() => {
    let filled = 0;
    let total = 0;
    const req = (ok) => { total += 2; if (ok) filled += 2; };
    const opt = (ok) => { total += 1; if (ok) filled += 1; };
    req((formData.firstName || '').trim().length >= 2);
    req((formData.lastName || '').trim().length >= 2);
    req(!!formData.autoGenerateId || !!(formData.nationalId || '').trim());
    opt(!!formData.dateOfBirth);
    opt(/^05\d{8}$/.test(formData.phone || ''));
    opt(!!formData.gender);
    return Math.round((filled / total) * 100);
  }, [formData.firstName, formData.lastName, formData.autoGenerateId, formData.nationalId, formData.dateOfBirth, formData.phone, formData.gender]);

  // Fetch checklists when modal opens
  useEffect(() => {
    if (showModal) {
      const fetchChecklistsData = async () => {
        try {
          const response = await api.get('/checklists');
          let checklistsList = [];
          if (Array.isArray(response)) {
            checklistsList = response;
          } else if (response.data && Array.isArray(response.data)) {
            checklistsList = response.data.data || response.data;
          } else if (response.success && Array.isArray(response.data)) {
            checklistsList = response.data;
          }
          setChecklists(checklistsList);
        } catch (err) {
          console.error('Error fetching checklists:', err);
        }
      };

      fetchChecklistsData();
    }
  }, [showModal]);

  // Fetch students when debounced search, year, pagination, or sorting changes
  useEffect(() => {
    // Don't fetch until the year context has resolved and a valid year is selected
    if (!yearResolved) return;

    const fetchStudentsData = async () => {
      setLoading(true);
      try {
        const params = {
          search: debouncedSearch,
          limit: pagination.pageSize,
          page: pagination.currentPage,
          offset: (pagination.currentPage - 1) * pagination.pageSize
        };
        if (isHairTransplant) {
          // `/transplant-clients` supports only search + status (+ pagination).
          // Year scoping and sorting are not meaningful for the flat transplant
          // client model, so skip those params.
          if (filters.status) params.status = filters.status;
        } else {
          if (selectedYear) {
            params.yearId = selectedYear;
          }
          if (sorting.column && sorting.direction) {
            params.sortBy = sorting.column;
            params.sortOrder = sorting.direction;
          }
          // Advanced filters
          if (filters.phone) params.phone = filters.phone;
          if (filters.class) params.class = filters.class;
          if (filters.grade) params.grade = filters.grade;
          if (filters.subjectId) params.subjectId = filters.subjectId;
          if (filters.groupId) params.groupId = filters.groupId;
          if (filters.notes) params.notes = filters.notes;
          if (filters.status) params.status = filters.status;
          if (filters.surveyStatus) params.surveyStatus = filters.surveyStatus;
          if (filters.dobFrom) params.dobFrom = filters.dobFrom;
          if (filters.dobTo) params.dobTo = filters.dobTo;
          if (hasClientStatus) {
            params.withStatusCounts = 1;
            if (sportStatus) params.status = sportStatus;
          }
        }
        const response = await api.get(listEndpoint, { params });
        if (hasClientStatus) setStatusCounts(response?.pagination?.statusCounts || response?.statusCounts || null);
        // Handle different response formats
        let studentsList = [];
        let totalItems = 0;
        if (Array.isArray(response)) {
          studentsList = response;
          // api.js unwraps to the inner array but preserves the server total on
          // response.pagination; fall back to page length if absent.
          totalItems = response.pagination?.total ?? response.length;
        } else if (response.data && Array.isArray(response.data)) {
          studentsList = response.data.data || response.data;
          // Server paginates: the authoritative total lives in `pagination.total`.
          // Falling back to the page length (studentsList.length) silently caps
          // the UI at one page and hides the rest of the records.
          totalItems = response.pagination?.total ?? response.data.total ?? response.total ?? studentsList.length;
        } else if (response.success && Array.isArray(response.data)) {
          studentsList = response.data;
          totalItems = response.pagination?.total ?? response.total ?? studentsList.length;
        }

        // Normalize transplant client records so the existing row renderers
        // (which read `firstName`/`lastName`) keep working. The transplant
        // collection stores a single `fullName` field instead.
        if (isHairTransplant) {
          studentsList = studentsList.map(c => {
            if (c.firstName || c.lastName) return c;
            const parts = String(c.fullName || '').trim().split(/\s+/);
            const firstName = parts.shift() || '';
            const lastName = parts.join(' ');
            return { ...c, firstName, lastName };
          });
        }

        setStudents(studentsList);
        setPagination(prev => ({
          ...prev,
          totalItems,
          totalPages: Math.ceil(totalItems / prev.pageSize)
        }));
      } catch (err) {
        console.error('Error fetching students:', err);
        error(t('errors.loadingFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchStudentsData();
  }, [yearResolved, debouncedSearch, selectedYear, pagination.currentPage, pagination.pageSize, sorting.column, sorting.direction, refetchTrigger, filters, listEndpoint, isHairTransplant, isSport, hasClientStatus, sportStatus]);

  // Fetch survey stats for each student on the current page (therapy only).
  // Hair-transplant clients live in a separate collection and have no
  // `/students/:id/survey-responses` route, so skip the survey fetch for them.
  useEffect(() => {
    if (!isTherapyCenter || isHairTransplant || students.length === 0) return;
    const fetchSurveyData = async () => {
      const map = {};
      const results = await Promise.allSettled(
        students.map(s => api.get(`/students/${s.id}/survey-responses`))
      );
      results.forEach((result, idx) => {
        const studentId = students[idx]?.id;
        if (result.status === 'fulfilled') {
          const data = Array.isArray(result.value) ? result.value : (result.value?.data || []);
          const completed = data.filter(d => d.isComplete).length;
          map[studentId] = { total: data.length, completed, pending: data.length - completed };
        } else {
          map[studentId] = { total: 0, completed: 0, pending: 0 };
        }
      });
      setSurveyDataMap(map);
      // Calculate total pending surveys across all visible students
      const totalPending = Object.values(map).reduce((sum, s) => sum + s.pending, 0);
      setTherapyStats(prev => ({ ...prev, pendingSurveys: totalPending, totalActive: pagination.totalItems }));
    };
    fetchSurveyData();
  }, [isTherapyCenter, students]);

  // Fetch the Quran goal status for each student on the current page (Quran
  // centers only). Lazy + per-page — there is no batch list endpoint, and the
  // page size bounds the fan-out. Mirrors the therapy survey-map effect above.
  useEffect(() => {
    if (!isQuran || students.length === 0) { setQuranStatusMap({}); return undefined; }
    let cancelled = false;
    (async () => {
      const results = await Promise.allSettled(students.map((s) => quranApi.studentStatus(s.id)));
      if (cancelled) return;
      const map = {};
      results.forEach((res, i) => {
        const id = students[i]?.id;
        if (!id) return;
        map[id] = res.status === 'fulfilled' ? (res.value?.data ?? null) : null;
      });
      setQuranStatusMap(map);
    })();
    return () => { cancelled = true; };
  }, [isQuran, students]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [debouncedSearch]);

  // Handle page change
  const handlePageChange = useCallback((page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
    setSelectedIds(new Set()); // Clear selection on page change
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback((size) => {
    setPagination(prev => ({ ...prev, pageSize: size, currentPage: 1 }));
    setSelectedIds(new Set());
  }, []);

  // Handle sorting
  const handleSort = useCallback((column, direction) => {
    setSorting({ column, direction });
  }, []);

  // Sport bulk "mark inactive / active" (PATCH /students/status, ≤500 ids).
  const applySportStatus = useCallback(async (ids, status) => {
    const studentIds = Array.from(ids);
    if (!studentIds.length || savingStatus) return;
    setSavingStatus(true);
    try {
      const res = await api.patch('/students/status', { studentIds, status });
      const updated = (res?.data?.updated || res?.updated || []).length;
      if (updated > 0) success(t('sport.clientStatus.bulkUpdated', { count: updated }));
      else success(t('sport.clientStatus.bulkNothing'));
      setSelectedIds(new Set());
      setRefetchTrigger(prev => prev + 1);
    } catch (err) {
      error(err?.response?.data?.message || err?.message || t('common.error'));
    } finally {
      setSavingStatus(false);
    }
  }, [savingStatus, success, error, t]);

  // Handle bulk actions
  const handleBulkAction = useCallback(async (action, ids) => {
    if (action === 'markInactive' || action === 'markActive') {
      await applySportStatus(ids, action === 'markInactive' ? 'inactive' : 'active');
    } else if (action === 'delete') {
      const ok = await confirm({
        title: t('common.delete'),
        message: t('students.bulkDeleteConfirm', { count: ids.size }),
        confirmText: t('common.delete'),
        isDangerous: true,
      });
      if (ok) {
        try {
          const idsArray = Array.from(ids);
          await Promise.all(idsArray.map(id => api.delete(`${listEndpoint}/${id}`)));
          success(t('students.bulkDeleteSuccess', { count: ids.size }));
          setSelectedIds(new Set());
          setRefetchTrigger(prev => prev + 1);
        } catch (err) {
          error(t('errors.deleteFailed'));
        }
      }
    } else if (action === 'export') {
      // Export selected students
      const selectedStudents = students.filter(s => ids.has(s.id));
      const csv = generateCSV(selectedStudents);
      downloadCSV(csv, `${isClinicCenter ? t('common.clients') : t('common.students')}_export.csv`);
      success(t('students.exportSuccess', { count: ids.size }));
    }
  }, [students, isClinicCenter, success, error, listEndpoint, t, confirm, applySportStatus]);

  // CSV Export helpers
  const generateCSV = (data) => {
    const headers = isClinicCenter
      ? [t('common.name'), t('common.phone'), t('common.email'), t('students.parentPhone'), t('common.status'), t('students.surveys')]
      : [t('common.name'), t('common.phone'), t('common.email'), t('students.parentPhone')];
    const rows = data.map(s => {
      const base = [studentFullName(s), s.phone || '', s.email || '', s.parentPhone || s.guardianMobile || ''];
      if (isClinicCenter) {
        const sv = surveyDataMap[s.id];
        base.push(s.status || 'active', sv ? `${sv.completed}/${sv.total}` : '\u2014');
      }
      return base;
    });
    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const downloadCSV = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv' });
    // saveFile: anchor download on web (unchanged), Filesystem+Share on native.
    saveFile(blob, filename, 'text/csv');
  };

  // Export every student matching the current year/search/filters (not just the
  // visible page) as a CSV the importer can read back.
  const [exportingAll, setExportingAll] = useState(false);
  const handleExportAllCSV = async () => {
    if (exportingAll) return;
    setExportingAll(true);
    try {
      const PAGE = 500;
      const baseParams = { search: debouncedSearch, limit: PAGE };
      if (selectedYear) baseParams.yearId = selectedYear;
      ['phone', 'class', 'grade', 'subjectId', 'groupId', 'notes', 'status', 'surveyStatus', 'dobFrom', 'dobTo']
        .forEach(key => { if (filters[key]) baseParams[key] = filters[key]; });
      if (hasClientStatus && sportStatus) baseParams.status = sportStatus;

      const all = [];
      for (let page = 1; ; page++) {
        const res = await api.get('/students', { params: { ...baseParams, page } });
        const list = Array.isArray(res) ? res : (res?.data || []);
        all.push(...list);
        const pages = res?.pagination?.pages ?? 1;
        if (page >= pages || list.length < PAGE) break;
      }
      if (all.length === 0) {
        error(t('students.importMap.exportEmpty', 'No students to export'));
        return;
      }
      const date = new Date().toISOString().slice(0, 10);
      await downloadCSVFile(buildStudentsExportCSV(all), `students_${date}.csv`);
      success(t('students.exportSuccess', { count: all.length }));
    } catch (err) {
      console.error('Error exporting students:', err);
      error(t('errors.loadingFailed'));
    } finally {
      setExportingAll(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    // Required fields
    if (!formData.firstName || formData.firstName.trim().length < 2) {
      errors.firstName = t('validation.firstNameRequired');
    }
    if (!formData.lastName || formData.lastName.trim().length < 2) {
      errors.lastName = t('validation.lastNameRequired');
    }

    // National ID validation (if not auto-generating)
    if (!formData.autoGenerateId && !formData.nationalId) {
      errors.nationalId = t('validation.nationalIdRequired');
    }

    // Mobile number validation (10 digits starting with 05)
    if (formData.phone) {
      const phoneRegex = /^05\d{8}$/;
      if (!phoneRegex.test(formData.phone)) {
        errors.phone = t('validation.invalidPhone');
      }
    }

    // Guardian mobile validation
    if (formData.guardianMobile) {
      const phoneRegex = /^05\d{8}$/;
      if (!phoneRegex.test(formData.guardianMobile)) {
        errors.guardianMobile = t('validation.invalidPhone');
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddStudent = async () => {
    try {
      let yearId = selectedYear;
      if (!yearId && years.length > 0) {
        yearId = years[0].id;
        setSelectedYear(yearId);
      }
      if (!yearId) {
        error(t('messages.selectYear'));
        return;
      }

      // Validate form
      if (!validateForm()) {
        error(t('validation.fixErrors'));
        return;
      }

      setIsSubmitting(true);

      const studentData = {
        ...formData,
        yearId: yearId
      };
      const createResponse = await api.post('/students', studentData);

      // Extract student ID from response
      let studentId = null;
      if (createResponse.data?.id) {
        studentId = createResponse.data.id;
      } else if (createResponse.id) {
        studentId = createResponse.id;
      }

      // Put the new student straight into the chosen group. A failure here
      // doesn't undo the add — the student exists, so warn and carry on.
      if (studentId && newStudentGroupId) {
        try {
          await api.post(`/groups/${newStudentGroupId}/students`, { studentId });
        } catch (err) {
          error(t('clientGroup.addFailed', { message: err?.response?.data?.message || err?.message || '' }));
        }
      }

      // Assign selected checklists to the new student
      if (studentId && selectedChecklists.length > 0) {
        for (const checklistId of selectedChecklists) {
          try {
            await api.post(`/students/${studentId}/assign-checklist`, { checklistId });
          } catch (err) {
            console.error(`Error assigning checklist ${checklistId}:`, err);
          }
        }
      }

      setFormData({
        nationalId: '',
        autoGenerateId: false,
        firstName: '',
        middleName: '',
        lastName: '',
        gender: '',
        dateOfBirth: '',
        phone: '',
        email: '',
        city: '',
        address: '',
        guardianName: '',
        guardianRelationship: '',
        guardianMobile: '',
        guardianId: '',
        parentPhone: ''
      });
      setValidationErrors({});
      setSelectedChecklists([]);
      setNewStudentGroupId('');
      setShowModal(false);

      // Reset to page 1 and trigger refetch so new student is visible
      setPagination(prev => ({ ...prev, currentPage: 1 }));
      setRefetchTrigger(prev => prev + 1);

      success(t('messages.studentAdded', { name: studentFullName(formData) }));
    } catch (err) {
      console.error('Error adding student:', err);
      error(t('errors.addStudentFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClick = (studentId) => {
    navigate(studentDetailPath(studentId));
  };

  const handleDeleteClick = (id, studentName) => {
    setStudentToDelete({ id, name: studentName });
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!studentToDelete) return;

    const deletedStudent = studentToDelete;

    try {
      // Optimistically remove from UI
      setStudents(prev => prev.filter(s => s.id !== deletedStudent.id));
      setShowDeleteConfirm(false);
      setStudentToDelete(null);

      await api.delete(`${listEndpoint}/${deletedStudent.id}`);

      // Show toast with undo option (if withUndo is available).
      // Note: the restore endpoint only exists for the learning/therapy
      // students collection. Transplant clients use a soft-delete so the
      // restore action below is skipped for them.
      if (withUndo && !isHairTransplant) {
        withUndo(
          t('messages.studentDeleted', { name: deletedStudent.name }),
          async () => {
            // Undo action - restore the student
            try {
              await api.post(`/students/${deletedStudent.id}/restore`);
              // Refresh the list
              setRefetchTrigger(prev => prev + 1);
              success(t('students.restored'));
            } catch (err) {
              error(t('errors.loadingFailed'));
            }
          }
        );
      } else {
        success(t('messages.studentDeleted', { name: deletedStudent.name }));
      }

      // Update pagination total
      setPagination(prev => ({
        ...prev,
        totalItems: Math.max(0, prev.totalItems - 1)
      }));
    } catch (err) {
      console.error('Error deleting student:', err);
      // Restore the student on error
      setRefetchTrigger(prev => prev + 1);
      error(t('errors.deleteStudentFailed'));
    }
  };

  // Group id → name lookup for the table column. Falls back to name/groupName.
  const groupNameById = useMemo(() => {
    const map = new Map();
    for (const g of groupsList) {
      map.set(g.id, g.name || g.groupName || '');
    }
    return map;
  }, [groupsList]);

  // Resolve a student's display group from the various shapes the API may return:
  // - groupName (string)
  // - groupId (string) -> lookup
  // - groups: [{ id, name }] (array, take first)
  const resolveStudentGroup = useCallback((s) => {
    if (s.groupName) return s.groupName;
    if (Array.isArray(s.groups) && s.groups.length > 0) {
      const g = s.groups[0];
      return g.name || g.groupName || groupNameById.get(g.id) || '';
    }
    if (s.groupId) return groupNameById.get(s.groupId) || '';
    return '';
  }, [groupNameById]);

  // Learning-center KPIs. "Total students" and "Active groups" / "Subjects" are
  // global counts. "New (30d)" is computed from the currently loaded page only,
  // so we mark it accordingly (an honest approximation — server doesn't expose
  // a count endpoint for this).
  const learningKpis = useMemo(() => {
    if (centerType !== 'learning') return null;
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    let newCount = 0;
    for (const s of students) {
      const c = s.createdAt;
      if (!c) continue;
      const ms = c?.toDate ? c.toDate().getTime() : new Date(c).getTime();
      if (Number.isFinite(ms) && ms >= thirtyDaysAgo) newCount += 1;
    }
    return {
      totalStudents: pagination.totalItems,
      activeGroups: groupsList.length,
      subjects: subjects.length,
      newThisMonth: newCount,
    };
  }, [centerType, students, pagination.totalItems, groupsList.length, subjects.length]);

  // Active grade chip (for quick-filter highlight).
  const activeGrade = filters.grade || '';

  // Yellow "inactive" row badge (learning centers; missing status = active).
  const sportInactiveBadge = (row) => (hasClientStatus && row?.status === 'inactive' ? (
    <span
      data-testid="sport-inactive-badge"
      title={t(inactiveHintKey)}
      style={{
        display: 'inline-flex', alignItems: 'center', padding: '0.125rem 0.5rem', borderRadius: '9999px',
        fontSize: '0.6875rem', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0,
        backgroundColor: 'var(--warning-100, #fef3c7)', color: 'var(--warning-700, #b45309)',
      }}
    >
      {t('sport.clientStatus.inactive')}
    </span>
  ) : null);

  // Quran centers: apply the memorization-status filters client-side over the
  // current page (the rollups are fetched per-page into quranStatusMap). Other
  // center types pass `students` through untouched.
  const visibleStudents = useMemo(() => {
    if (!isQuran) return students;
    const { status, behind, noGoal } = quranFilters;
    if (!status && !behind && !noGoal) return students;
    return students.filter((s) => {
      const st = quranStatusMap[s.id];
      if (status && !quranHasSurahStatus(st, status)) return false;
      if (behind && !quranIsBehind(st)) return false;
      if (noGoal && quranHasGoal(st)) return false;
      return true;
    });
  }, [isQuran, students, quranStatusMap, quranFilters]);

  // Count of active Quran filters (for the chip-row "clear" affordance).
  const quranActiveFilterCount = (quranFilters.status ? 1 : 0)
    + (quranFilters.behind ? 1 : 0) + (quranFilters.noGoal ? 1 : 0);

  return (
    <div data-students-page style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '0.75rem' : '1.5rem', direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Global animations + page-scoped table polish */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        /* Hover row accent — a 3px primary edge on hover so the eye locks onto
           the active row. LTR: left edge. RTL: right edge. Scoped to this page
           via [data-students-page] so other tables stay unchanged. */
        [data-students-page] .enterprise-table-row {
          box-shadow: inset 0 0 0 0 var(--color-primary);
          transition: box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1);
        }
        [data-students-page] .enterprise-table-row:hover {
          box-shadow: inset 3px 0 0 0 var(--color-primary);
        }
        [dir="rtl"] [data-students-page] .enterprise-table-row:hover,
        [data-students-page][dir="rtl"] .enterprise-table-row:hover {
          box-shadow: inset -3px 0 0 0 var(--color-primary);
        }
      `}</style>
      <Breadcrumb />

      {/* Birthday-gift data-quality banner: appears only when some clients are missing gender/DOB */}
      {birthdayIssueCount > 0 && !birthdayBannerDismissed && (
        <div style={{
          display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
          padding: '0.85rem 1rem',
          backgroundColor: 'var(--warning-50)',
          border: '1px solid var(--warning-100)',
          color: 'var(--warning-700)',
          borderRadius: 10, marginBottom: '0.75rem',
        }}>
          <span style={{ fontSize: '1.1rem', flexShrink: 0 }} aria-hidden="true">⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
              {t('birthdayGifts.banner.title', { count: birthdayIssueCount })}
            </div>
            <div style={{ fontSize: '0.82rem' }}>
              {t('birthdayGifts.banner.description')}
            </div>
            <button
              type="button"
              onClick={() => navigate('/birthday-gifts')}
              style={{
                marginTop: '0.5rem', padding: '0.35rem 0.85rem',
                borderRadius: 6, border: '1px solid var(--warning-600)',
                backgroundColor: 'var(--warning-500)', color: 'var(--text-inverse)',
                fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              {t('birthdayGifts.banner.cta')}
            </button>
          </div>
          <button
            type="button"
            onClick={() => {
              setBirthdayBannerDismissed(true);
              try { sessionStorage.setItem('birthdayBannerDismissed', '1'); } catch { /* noop */ }
            }}
            aria-label="dismiss"
            style={{ border: 'none', background: 'transparent', color: 'var(--warning-700)', cursor: 'pointer', fontSize: '1.1rem' }}
          >
            ×
          </button>
        </div>
      )}

      {/* KPI Cards - Full grid on desktop, compact summary on mobile */}
      {isClinicCenter && !isMobile && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '1rem'
        }}>
          <KPICard
            icon="people"
            iconColor="#0ea5e9"
            iconBg="rgba(14, 165, 233, 0.1)"
            label={isHairTransplant ? (t('transplantCrm.totalClients')) : (t('clientProgress.totalActiveClients'))}
            value={pagination.totalItems}
            isRTL={isRTL}
          />
          <KPICard
            icon={isHairTransplant ? 'medical_services' : 'event_note'}
            iconColor="#8b5cf6"
            iconBg="rgba(139, 92, 246, 0.1)"
            label={isHairTransplant ? (t('transplantCrm.upcomingTreatments')) : (t('clientProgress.sessionsThisWeek'))}
            value={"\u2014"}
            isRTL={isRTL}
          />
          <KPICard
            icon={isHairTransplant ? 'healing' : 'assignment_late'}
            iconColor={isHairTransplant ? '#0ea5e9' : therapyStats.pendingSurveys > 0 ? 'var(--warning-500)' : 'var(--success-500)'}
            iconBg={isHairTransplant ? 'rgba(14, 165, 233, 0.1)' : therapyStats.pendingSurveys > 0 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(34, 197, 94, 0.1)'}
            label={isHairTransplant ? (t('transplantCrm.postOpFollowUps')) : (t('clientProgress.pendingSurveys'))}
            value={isHairTransplant ? '\u2014' : therapyStats.pendingSurveys}
            isRTL={isRTL}
          />
          <KPICard
            icon="person_add"
            iconColor="#22c55e"
            iconBg="rgba(34, 197, 94, 0.1)"
            label={t('clientProgress.newThisMonth')}
            value={"\u2014"}
            isRTL={isRTL}
          />
        </div>
      )}
      {/* Learning-center KPI strip — desktop. */}
      {centerType === 'learning' && !isMobile && learningKpis && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '1rem'
        }}>
          <KPICard
            icon={Users}
            iconColor="#0ea5e9"
            iconBg="rgba(14, 165, 233, 0.1)"
            label={t('students.title')}
            value={learningKpis.totalStudents}
            isRTL={isRTL}
          />
          <KPICard
            icon={Sparkles}
            iconColor="#22c55e"
            iconBg="rgba(34, 197, 94, 0.1)"
            label={t('students.newThisMonth', 'New (30d)')}
            value={learningKpis.newThisMonth}
            isRTL={isRTL}
          />
          <KPICard
            icon={Layers}
            iconColor="#8b5cf6"
            iconBg="rgba(139, 92, 246, 0.1)"
            label={t('students.activeGroups', 'Active Groups')}
            value={learningKpis.activeGroups}
            isRTL={isRTL}
          />
          <KPICard
            icon={BookOpen}
            iconColor="#f59e0b"
            iconBg="rgba(245, 158, 11, 0.1)"
            label={t('students.subjects', 'Subjects')}
            value={learningKpis.subjects}
            isRTL={isRTL}
          />
        </div>
      )}
      {/* Learning-center compact summary — mobile. */}
      {centerType === 'learning' && isMobile && learningKpis && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.5rem 0.75rem', borderRadius: '8px',
          backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)',
          fontSize: '0.75rem', color: 'var(--color-text-secondary)',
          direction: isRTL ? 'rtl' : 'ltr', flexWrap: 'wrap',
        }}>
          <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
            {learningKpis.totalStudents} {t('students.title')}
          </span>
          {learningKpis.newThisMonth > 0 && (
            <span style={{ color: 'var(--success-500)' }}>• +{learningKpis.newThisMonth} {t('students.newThisMonth', 'new')}</span>
          )}
          {learningKpis.activeGroups > 0 && (
            <span>• {learningKpis.activeGroups} {t('students.groups', 'groups')}</span>
          )}
        </div>
      )}
      {/* Mobile: Compact summary line replacing 4 cards */}
      {isClinicCenter && isMobile && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.5rem 0.75rem', borderRadius: '8px',
          backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)',
          fontSize: '0.75rem', color: 'var(--color-text-secondary)',
          direction: isRTL ? 'rtl' : 'ltr', flexWrap: 'wrap',
        }}>
          <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
            {pagination.totalItems} {isHairTransplant ? (t('transplantCrm.totalClients')) : (t('students.title'))}
          </span>
          {!isHairTransplant && therapyStats.pendingSurveys > 0 && (
            <span style={{ color: 'var(--warning-500)' }}>• {therapyStats.pendingSurveys} {t('clientProgress.pendingSurveys', 'Pending')}</span>
          )}
        </div>
      )}
      {/* Page Header: Title + Actions */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'row' : 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.5rem'
      }}>
        <h1 style={{
          fontSize: isMobile ? '1.125rem' : '1.875rem',
          fontWeight: '700',
          color: 'var(--color-text-primary)',
          textAlign: isRTL ? 'right' : 'left',
          margin: 0,
          flex: 1,
        }}>{t('students.title')}</h1>
        <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center', flexShrink: 0 }}>
          <button
            onClick={() => {
              if (isHairTransplant) {
                navigate('/transplant-crm');
              } else {
                setShowModal(true);
              }
            }}
            style={{
              backgroundColor: 'var(--color-primary)',
              border: 'none',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              color: 'var(--color-on-primary, #fff)',
              padding: isMobile ? '0.375rem 0.625rem' : '0.5rem 1rem',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
              transition: 'all 0.2s ease',
              fontSize: isMobile ? '0.75rem' : '0.875rem'
            }}
          >
            <UserPlus size={isMobile ? 14 : 16} />
            {isMobile ? t('common.add', 'Add') : t('students.addStudent')}
            {!isMobile && centerType === 'learning' && !isHairTransplant && (
              <kbd
                aria-hidden="true"
                title={t('students.shortcutAdd', 'Press N to add')}
                style={{
                  marginInlineStart: '0.5rem',
                  padding: '0.0625rem 0.375rem',
                  borderRadius: '0.25rem',
                  backgroundColor: 'rgba(255,255,255,0.18)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  lineHeight: 1.2,
                }}
              >N</kbd>
            )}
          </button>
          {!isHairTransplant && (
            <>
              <button
                type="button"
                onClick={handleExportAllCSV}
                disabled={exportingAll}
                title={t('students.importMap.exportCsv', 'Export students (CSV)')}
                aria-label={t('students.importMap.exportCsv', 'Export students (CSV)')}
                style={headerSecondaryBtnStyle(isMobile, exportingAll)}
              >
                {exportingAll ? <Loader2 size={isMobile ? 14 : 16} className="animate-spin" /> : <Download size={isMobile ? 14 : 16} />}
                {!isMobile && t('students.importMap.exportCsv', 'Export students (CSV)')}
              </button>
              {!isMobile && (
                <button
                  type="button"
                  onClick={() => setShowImportModal(true)}
                  title={t('students.importMap.importCsv', 'Import students (CSV)')}
                  style={headerSecondaryBtnStyle(isMobile, false)}
                >
                  <Upload size={16} />
                  {t('students.importMap.importCsv', 'Import students (CSV)')}
                </button>
              )}
            </>
          )}
          {/* More actions dropdown */}
          <ActionMenu
            actions={[
              ...(isMobile ? [] : [
                ...(isHairTransplant ? [{
                  label: t('students.importMap.menuLabel') || t('students.importCSV') || 'Import (CSV/Excel)',
                  icon: Upload,
                  onClick: () => setShowImportModal(true)
                }] : []),
                {
                  label: t('students.downloadTemplate'),
                  icon: Download,
                  onClick: () => downloadCSVFile(generateTemplateCSV(), 'student_template.csv')
                }
              ]),
              ...(!isHairTransplant ? [{
                label: t('duplicates.detect', 'Detect duplicates'),
                icon: CopyCheck,
                onClick: () => setShowDuplicates(true)
              }] : []),
              {
                label: t('common.exportPdf'),
                icon: FileTextIcon,
                onClick: () => {
                  // Trigger PDF export programmatically
                  const btn = document.querySelector('[data-export-pdf]');
                  if (btn) btn.click();
                }
              }
            ]}
          />
          {/* Hidden PDF export trigger */}
          <div style={{ display: 'none' }}>
            <ExportPdfButton
              reportType="clients"
              variant="outline"
              data-export-pdf
              getReportData={() => ({
                centerName: '',
                clients: students,
              })}
            />
          </div>
        </div>
      </div>

      {/* Unified CRM Toolbar: Search + Filters + Meta.
          On desktop learning, sticks to the top of the scroll container so the
          search/filters stay reachable while scrolling long rosters. */}
      <div style={{
        backgroundColor: 'var(--color-surface)',
        borderRadius: '0.75rem',
        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        border: '1px solid var(--color-border)',
        overflow: 'hidden',
        direction: isRTL ? 'rtl' : 'ltr',
        ...(!isMobile && centerType === 'learning' ? {
          position: 'sticky',
          top: 0,
          zIndex: 4,
        } : {}),
      }}>
        {/* Row 1: Search + Filter toggle */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: isMobile ? '0.625rem 0.75rem' : '0.75rem 1rem'
        }}>
          {/* Search */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flex: 1,
            backgroundColor: 'var(--color-surface-alt)',
            borderRadius: '0.5rem',
            padding: '0.375rem 0.75rem',
            border: '1px solid var(--color-border)',
            transition: 'border-color 0.2s ease'
          }}>
            {isSearching ? (
              <Loader2 size={16} style={{ color: 'var(--color-primary)', animation: 'spin 1s linear infinite', flexShrink: 0 }} />
            ) : (
              <Search size={16} style={{ color: 'var(--color-text-tertiary)', flexShrink: 0 }} />
            )}
            <input
              ref={searchInputRef}
              type="text"
              aria-label={t('common.search')}
              placeholder={t('common.search') + '...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                backgroundColor: 'transparent',
                flex: 1,
                outline: 'none',
                border: 'none',
                padding: '0.25rem 0',
                fontSize: '0.875rem',
                direction: isRTL ? 'rtl' : 'ltr',
                color: 'var(--color-text-primary)',
                minWidth: 0
              }}
            />
            {!search && !isMobile && centerType === 'learning' && (
              <kbd
                aria-hidden="true"
                title={t('students.shortcutSearch', 'Press / to search')}
                style={{
                  padding: '0.0625rem 0.375rem',
                  borderRadius: '0.25rem',
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  color: 'var(--color-text-tertiary)',
                  lineHeight: 1.2,
                  flexShrink: 0,
                }}
              >/</kbd>
            )}
            {search && (
              <button
                onClick={() => setSearch('')}
                aria-label={t('common.clearSearch')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.125rem',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--color-text-tertiary)',
                  flexShrink: 0
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>
          {/* Filter toggle */}
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            aria-label={t('common.filter')}
            title={t('common.filter')}
            aria-expanded={showFilters}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.5rem 0.75rem',
              backgroundColor: showFilters ? 'rgba(53, 87, 96, 0.08)' : 'transparent',
              border: '1px solid var(--color-border)',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontSize: '0.8125rem',
              fontWeight: '600',
              color: showFilters ? 'var(--color-primary)' : 'var(--color-text-secondary)',
              flexShrink: 0,
              transition: 'all 0.15s ease'
            }}
          >
            <SlidersHorizontal size={15} />
            {!isMobile && <span>{t('common.filter')}</span>}
            {Object.values(filters).filter(v => v).length > 0 && (
              <span style={{
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-on-primary, #fff)',
                fontSize: '0.625rem',
                fontWeight: '700',
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1
              }}>
                {Object.values(filters).filter(v => v).length}
              </span>
            )}
          </button>
          {/* Density toggle (desktop/tablet only — mobile uses cards) */}
          {!isMobile && centerType === 'learning' && (
            <button
              onClick={() => setDensity((d) => d === 'compact' ? 'comfortable' : 'compact')}
              aria-label={t('students.toggleDensity', 'Toggle row density')}
              title={density === 'compact' ? t('students.densityComfortable', 'Comfortable rows') : t('students.densityCompact', 'Compact rows')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.5rem',
                backgroundColor: 'transparent',
                border: '1px solid var(--color-border)',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                color: 'var(--color-text-secondary)',
                flexShrink: 0,
                transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {density === 'compact' ? <Rows3 size={15} /> : <Rows2 size={15} />}
            </button>
          )}
        </div>

        {/* Row 2: Expanded filters — always rendered, animated via max-height
            so the drawer eases open/closed instead of popping. */}
        <div
          aria-hidden={!showFilters}
          style={{
            maxHeight: showFilters ? '480px' : '0px',
            opacity: showFilters ? 1 : 0,
            overflow: 'hidden',
            visibility: showFilters ? 'visible' : 'hidden',
            transition: 'max-height 220ms cubic-bezier(0.4, 0, 0.2, 1), opacity 180ms ease, visibility 0s linear ' + (showFilters ? '0s' : '220ms'),
          }}
        >
          <div style={{
            padding: '0.75rem 1rem',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.75rem',
            borderTop: '1px solid var(--color-border)',
            alignItems: 'flex-end'
          }}>
            {isClinicCenter ? (
              <>
                {/* Phone */}
                <div style={{ minWidth: isMobile ? '100%' : '160px', flex: isMobile ? '1 1 100%' : '0 1 auto' }}>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('common.phone')}
                  </label>
                  <input type="text" value={filters.phone} onChange={(e) => setFilters(f => ({ ...f, phone: e.target.value }))} placeholder="05..."
                    style={{ width: '100%', padding: '0.4rem 0.625rem', border: '1px solid var(--color-border)', borderRadius: '0.375rem', fontSize: '0.8125rem', fontFamily: 'inherit', boxSizing: 'border-box', backgroundColor: 'var(--color-surface)' }} />
                </div>
                {/* Status */}
                <div style={{ minWidth: isMobile ? '100%' : '150px', flex: isMobile ? '1 1 100%' : '0 1 auto' }}>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('common.status')}
                  </label>
                  <select value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
                    style={{ width: '100%', padding: '0.4rem 0.625rem', border: '1px solid var(--color-border)', borderRadius: '0.375rem', fontSize: '0.8125rem', fontFamily: 'inherit', backgroundColor: 'var(--color-surface)', boxSizing: 'border-box' }}>
                    <option value="">{t('common.all')}</option>
                    {isHairTransplant ? (
                      <>
                        <option value="profile_saved">{t('transplantCrm.statuses.profile_saved')}</option>
                        <option value="quote_sent">{t('transplantCrm.statuses.quote_sent')}</option>
                        <option value="client_approved">{t('transplantCrm.statuses.client_approved')}</option>
                        <option value="surgery_scheduled">{t('transplantCrm.statuses.surgery_scheduled')}</option>
                        <option value="cancelled">{t('transplantCrm.statuses.cancelled')}</option>
                      </>
                    ) : (
                      <>
                        <option value="active">{t('common.active')}</option>
                        <option value="on_hold">{t('common.onHold')}</option>
                        <option value="discharged">{t('common.discharged')}</option>
                        <option value="waitlist">{t('common.waitlist')}</option>
                      </>
                    )}
                  </select>
                </div>
                {/* Survey Status (therapy only, not hair transplant) */}
                {isTherapyCenter && (
                  <div style={{ minWidth: isMobile ? '100%' : '150px', flex: isMobile ? '1 1 100%' : '0 1 auto' }}>
                    <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {t('students.surveyStatus')}
                    </label>
                    <select value={filters.surveyStatus} onChange={(e) => setFilters(f => ({ ...f, surveyStatus: e.target.value }))}
                      style={{ width: '100%', padding: '0.4rem 0.625rem', border: '1px solid var(--color-border)', borderRadius: '0.375rem', fontSize: '0.8125rem', fontFamily: 'inherit', backgroundColor: 'var(--color-surface)', boxSizing: 'border-box' }}>
                      <option value="">{t('common.all')}</option>
                      <option value="has_pending">{t('students.hasPending')}</option>
                      <option value="all_completed">{t('students.allCompleted')}</option>
                      <option value="no_surveys">{t('students.noSurveys')}</option>
                    </select>
                  </div>
                )}
                {/* Notes */}
                <div style={{ minWidth: isMobile ? '100%' : '160px', flex: isMobile ? '1 1 100%' : '0 1 auto' }}>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('students.notes')}
                  </label>
                  <input type="text" value={filters.notes} onChange={(e) => setFilters(f => ({ ...f, notes: e.target.value }))}
                    placeholder={t('students.notesPlaceholder', 'Search note title or content')}
                    style={{ width: '100%', padding: '0.4rem 0.625rem', border: '1px solid var(--color-border)', borderRadius: '0.375rem', fontSize: '0.8125rem', fontFamily: 'inherit', boxSizing: 'border-box', backgroundColor: 'var(--color-surface)' }} />
                </div>
                {/* DOB From — inclusive lower bound. Hair-transplant clients
                    don't have a DOB column on the list endpoint, so the
                    backend filter is a no-op there; kept for parity with
                    therapy clients. */}
                <div style={{ minWidth: isMobile ? '100%' : '160px', flex: isMobile ? '1 1 100%' : '0 1 auto' }}>
                  <label htmlFor="filter-dob-from" style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('students.dobFrom', 'Born from')}
                  </label>
                  <input
                    id="filter-dob-from"
                    type="date"
                    value={filters.dobFrom}
                    max={filters.dobTo || undefined}
                    onChange={(e) => setFilters(f => ({ ...f, dobFrom: e.target.value }))}
                    style={{ width: '100%', padding: '0.4rem 0.625rem', border: '1px solid var(--color-border)', borderRadius: '0.375rem', fontSize: '0.8125rem', fontFamily: 'inherit', boxSizing: 'border-box', backgroundColor: 'var(--color-surface)' }}
                  />
                </div>
                {/* DOB To — inclusive upper bound */}
                <div style={{ minWidth: isMobile ? '100%' : '160px', flex: isMobile ? '1 1 100%' : '0 1 auto' }}>
                  <label htmlFor="filter-dob-to" style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('students.dobTo', 'Born to')}
                  </label>
                  <input
                    id="filter-dob-to"
                    type="date"
                    value={filters.dobTo}
                    min={filters.dobFrom || undefined}
                    onChange={(e) => setFilters(f => ({ ...f, dobTo: e.target.value }))}
                    style={{ width: '100%', padding: '0.4rem 0.625rem', border: '1px solid var(--color-border)', borderRadius: '0.375rem', fontSize: '0.8125rem', fontFamily: 'inherit', boxSizing: 'border-box', backgroundColor: 'var(--color-surface)' }}
                  />
                </div>
              </>
            ) : (
              <>
                {/* Phone */}
                <div style={{ minWidth: isMobile ? '100%' : '160px', flex: isMobile ? '1 1 100%' : '0 1 auto' }}>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('common.phone')}
                  </label>
                  <input type="text" value={filters.phone} onChange={(e) => setFilters(f => ({ ...f, phone: e.target.value }))} placeholder="05..."
                    style={{ width: '100%', padding: '0.4rem 0.625rem', border: '1px solid var(--color-border)', borderRadius: '0.375rem', fontSize: '0.8125rem', fontFamily: 'inherit', boxSizing: 'border-box', backgroundColor: 'var(--color-surface)' }} />
                </div>
                {/* Subject */}
                <div style={{ minWidth: isMobile ? '100%' : '150px', flex: isMobile ? '1 1 100%' : '0 1 auto' }}>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('groups.subject')}
                  </label>
                  <select value={filters.subjectId} onChange={(e) => setFilters(f => ({ ...f, subjectId: e.target.value }))}
                    style={{ width: '100%', padding: '0.4rem 0.625rem', border: '1px solid var(--color-border)', borderRadius: '0.375rem', fontSize: '0.8125rem', fontFamily: 'inherit', backgroundColor: 'var(--color-surface)', boxSizing: 'border-box' }}>
                    <option value="">{t('common.all')}</option>
                    {subjects.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
                  </select>
                </div>
                {/* Group */}
                <div style={{ minWidth: isMobile ? '100%' : '150px', flex: isMobile ? '1 1 100%' : '0 1 auto' }}>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('students.group')}
                  </label>
                  <select value={filters.groupId} onChange={(e) => setFilters(f => ({ ...f, groupId: e.target.value }))}
                    style={{ width: '100%', padding: '0.4rem 0.625rem', border: '1px solid var(--color-border)', borderRadius: '0.375rem', fontSize: '0.8125rem', fontFamily: 'inherit', backgroundColor: 'var(--color-surface)', boxSizing: 'border-box' }}>
                    <option value="">{t('common.all')}</option>
                    {groupsList.map(g => (<option key={g.id} value={g.id}>{g.name || g.groupName}</option>))}
                  </select>
                </div>
                {/* Grade */}
                {!isSport && <div style={{ minWidth: isMobile ? '100%' : '100px', flex: isMobile ? '1 1 100%' : '0 1 auto' }}>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('students.grade')}
                  </label>
                  <select value={filters.grade} onChange={(e) => setFilters(f => ({ ...f, grade: e.target.value }))}
                    style={{ width: '100%', padding: '0.4rem 0.625rem', border: '1px solid var(--color-border)', borderRadius: '0.375rem', fontSize: '0.8125rem', fontFamily: 'inherit', backgroundColor: 'var(--color-surface)', boxSizing: 'border-box' }}>
                    <option value="">{t('common.all')}</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(g => (<option key={g} value={g}>{g}</option>))}
                  </select>
                </div>}
                {/* Class (school class — not meaningful for sport clients) */}
                {!isSport && <div style={{ minWidth: isMobile ? '100%' : '120px', flex: isMobile ? '1 1 100%' : '0 1 auto' }}>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('students.class')}
                  </label>
                  <input type="text" value={filters.class} onChange={(e) => setFilters(f => ({ ...f, class: e.target.value }))}
                    style={{ width: '100%', padding: '0.4rem 0.625rem', border: '1px solid var(--color-border)', borderRadius: '0.375rem', fontSize: '0.8125rem', fontFamily: 'inherit', boxSizing: 'border-box', backgroundColor: 'var(--color-surface)' }} />
                </div>}
                {/* Notes */}
                <div style={{ minWidth: isMobile ? '100%' : '160px', flex: isMobile ? '1 1 100%' : '0 1 auto' }}>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('students.notes')}
                  </label>
                  <input type="text" value={filters.notes} onChange={(e) => setFilters(f => ({ ...f, notes: e.target.value }))}
                    placeholder={t('students.notesPlaceholder', 'Search note title or content')}
                    style={{ width: '100%', padding: '0.4rem 0.625rem', border: '1px solid var(--color-border)', borderRadius: '0.375rem', fontSize: '0.8125rem', fontFamily: 'inherit', boxSizing: 'border-box', backgroundColor: 'var(--color-surface)' }} />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Row 3: Filter chips + Showing count */}
        {(Object.values(filters).some(v => v) || (!loading && pagination.totalItems > 0) || debouncedSearch) && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.375rem',
            padding: '0.5rem 1rem',
            borderTop: '1px solid var(--color-border)',
            minHeight: '32px'
          }}>
            {/* Active filter chips */}
            {Object.entries(filters).filter(([, v]) => v).map(([key, value]) => {
              let label = key;
              let displayVal = value;
              if (key === 'phone') label = t('common.phone');
              else if (key === 'status') label = t('common.status');
              else if (key === 'surveyStatus') label = t('students.surveyStatus');
              else if (key === 'subjectId') { label = t('groups.subject'); displayVal = subjects.find(s => s.id === value)?.name || value; }
              else if (key === 'groupId') { label = t('students.group'); displayVal = groupsList.find(g => g.id === value)?.name || groupsList.find(g => g.id === value)?.groupName || value; }
              else if (key === 'grade') label = t('students.grade');
              else if (key === 'class') label = t('students.class');
              else if (key === 'notes') label = t('students.notes');
              else if (key === 'dobFrom') label = t('students.dobFrom', 'Born from');
              else if (key === 'dobTo') label = t('students.dobTo', 'Born to');
              return (
                <span key={key} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                  padding: '0.1875rem 0.5rem', backgroundColor: 'rgba(53, 87, 96, 0.08)',
                  borderRadius: '1rem', fontSize: '0.75rem', fontWeight: '500', color: 'var(--color-primary)'
                }}>
                  {label}: {displayVal}
                  <button onClick={() => setFilters(f => ({ ...f, [key]: '' }))}
                    aria-label={t('common.removeFilter')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'var(--color-primary)', marginInlineStart: '0.125rem' }}>
                    <X size={12} />
                  </button>
                </span>
              );
            })}
            {/* Clear all */}
            {Object.values(filters).filter(v => v).length > 1 && (
              <button
                onClick={() => setFilters(isClinicCenter
                  ? { phone: '', status: '', surveyStatus: '', notes: '', dobFrom: '', dobTo: '' }
                  : { phone: '', class: '', grade: '', subjectId: '', groupId: '', notes: '' }
                )}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '0.75rem', fontWeight: '600', color: 'var(--color-danger)',
                  padding: '0.125rem 0.25rem', textDecoration: 'underline'
                }}
              >
                {t('student.clearFilters')}
              </button>
            )}
            {/* Spacer + showing count */}
            <span style={{ flex: 1 }} />
            {!loading && (
              <span style={{
                fontSize: '0.75rem', color: 'var(--color-text-tertiary)', fontWeight: '500', whiteSpace: 'nowrap'
              }}>
                {debouncedSearch
                  ? (pagination.totalItems > 0
                    ? `${pagination.totalItems} ${t('pagination.results')}`
                    : t('students.noResults'))
                  : `${t('pagination.showing')} ${students.length} ${t('pagination.of')} ${pagination.totalItems}`
                }
              </span>
            )}
          </div>
        )}
      </div>

      {/* Active / inactive chips with counts ("فعّال N · غير فعّال M · الكل"),
          every learning center. Counts come from the server over every other
          filter. */}
      {hasClientStatus && (
        <div
          role="tablist"
          aria-label={t('sport.clientStatus.label')}
          data-testid="sport-status-chips"
          style={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.375rem',
            padding: isMobile ? '0.25rem 0' : '0.125rem 0',
            direction: isRTL ? 'rtl' : 'ltr',
          }}
        >
          {[
            { key: 'active', label: t('sport.clientStatus.active'), count: statusCounts?.active },
            { key: 'inactive', label: t('sport.clientStatus.inactive'), count: statusCounts?.inactive },
            { key: '', label: t('sport.clientStatus.all'), count: statusCounts?.total },
          ].map(({ key, label, count }) => {
            const isActive = sportStatus === key;
            const yellow = key === 'inactive';
            return (
              <button
                key={key || 'all'}
                type="button"
                role="tab"
                aria-selected={isActive}
                data-testid={`sport-status-chip-${key || 'all'}`}
                onClick={() => {
                  setSportStatus(key);
                  setSelectedIds(new Set());
                  setPagination(prev => ({ ...prev, currentPage: 1 }));
                }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                  minHeight: 32,
                  padding: '0.3125rem 0.75rem',
                  borderRadius: '9999px',
                  border: `1px solid ${isActive ? (yellow ? 'var(--warning-500, #f59e0b)' : 'var(--color-primary)') : 'var(--color-border)'}`,
                  backgroundColor: isActive
                    ? (yellow ? 'var(--warning-100, #fef3c7)' : 'var(--color-primary)')
                    : 'var(--color-surface)',
                  color: isActive ? (yellow ? 'var(--warning-700, #b45309)' : 'var(--color-on-primary, #fff)') : 'var(--color-text-secondary)',
                  fontSize: '0.8125rem',
                  fontWeight: isActive ? 700 : 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                }}
              >
                {yellow && !isActive && (
                  <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'var(--warning-500, #f59e0b)' }} />
                )}
                {label}
                {typeof count === 'number' && <span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.85 }}>{count}</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Mobile bulk bar (learning managers): the cards carry their own
          checkboxes; the desktop table has its built-in bulk bar. */}
      {isMobile && canManageStatus && selectedIds.size > 0 && (
        <div
          role="toolbar"
          data-testid="sport-status-bulk-bar"
          style={{
            position: 'sticky', top: 0, zIndex: 5,
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem',
            padding: '0.5rem 0.75rem', borderRadius: '0.75rem',
            backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            direction: isRTL ? 'rtl' : 'ltr',
          }}
        >
          <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-primary)', marginInlineEnd: 'auto' }}>
            {t('sport.clientStatus.selected', { count: selectedIds.size })}
          </span>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={savingStatus}
            onClick={() => applySportStatus(selectedIds, 'inactive')}
            data-testid="sport-status-bulk-inactive"
            style={{ minHeight: 44, backgroundColor: 'var(--warning-100, #fef3c7)', color: 'var(--warning-700, #b45309)', border: '1px solid var(--warning-500, #f59e0b)' }}
          >
            {t('sport.clientStatus.bulkMarkInactive')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={savingStatus}
            onClick={() => applySportStatus(selectedIds, 'active')}
            data-testid="sport-status-bulk-active"
            style={{ minHeight: 44 }}
          >
            {t('sport.clientStatus.bulkMarkActive')}
          </button>
          <button
            type="button"
            aria-label={t('sport.clientStatus.clearSelection')}
            title={t('sport.clientStatus.clearSelection')}
            onClick={() => setSelectedIds(new Set())}
            style={{ minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-tertiary)' }}
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Grade quick-filter chips (learning center only). One-click filter for
          the most common education-admin task: scoping the list by grade. */}
      {centerType === 'learning' && !isSport && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            overflowX: 'auto',
            padding: isMobile ? '0.25rem 0' : '0.125rem 0',
            direction: isRTL ? 'rtl' : 'ltr',
            scrollbarWidth: 'none',
          }}
          role="tablist"
          aria-label={t('students.filterByGrade', 'Filter by grade')}
        >
          <span style={{
            fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-text-tertiary)',
            textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
            paddingInlineEnd: '0.375rem',
          }}>
            {t('students.grade', 'Grade')}
          </span>
          {[''].concat(Array.from({ length: 12 }, (_, i) => String(i + 1))).map((g) => {
            const isActive = activeGrade === g;
            const label = g === '' ? (t('common.all', 'All')) : `G${g}`;
            return (
              <button
                key={g || 'all'}
                role="tab"
                aria-selected={isActive}
                onClick={() => setFilters((f) => ({ ...f, grade: g }))}
                style={{
                  flexShrink: 0,
                  padding: '0.3125rem 0.75rem',
                  borderRadius: '9999px',
                  border: `1px solid ${isActive ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  backgroundColor: isActive ? 'var(--color-primary)' : 'var(--color-surface)',
                  color: isActive ? 'var(--color-on-primary, #fff)' : 'var(--color-text-secondary)',
                  fontSize: '0.75rem',
                  fontWeight: isActive ? 700 : 500,
                  cursor: 'pointer',
                  transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Quran (Hifz) memorization filters (Quran centers only). */}
      {isQuran && (
        <div
          style={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap',
            gap: '0.5rem',
            padding: isMobile ? '0.25rem 0' : '0.125rem 0',
            direction: isRTL ? 'rtl' : 'ltr',
          }}
        >
          <span style={{
            fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-text-tertiary)',
            textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
            paddingInlineEnd: '0.25rem',
          }}>
            {t('quran.progress', 'Memorization')}
          </span>
          <select
            value={quranFilters.status}
            onChange={(e) => setQuranFilters((f) => ({ ...f, status: e.target.value }))}
            aria-label={t('quran.filterStatus', 'Status')}
            style={{
              padding: '0.3125rem 0.625rem', borderRadius: '9999px',
              border: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)',
              color: 'var(--color-text-secondary)', fontSize: '0.75rem', fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <option value="">{t('quran.allStatuses', 'All statuses')}</option>
            {MASTERY_STATUSES.map((s) => (
              <option key={s} value={s}>{t(`quran.${STATUS_KEY_SUFFIX[s]}`, s)}</option>
            ))}
          </select>
          {[
            { key: 'behind', label: t('quran.behindPlan', 'Behind plan') },
            { key: 'noGoal', label: t('quran.noGoalSet', 'No goal set') },
          ].map(({ key, label }) => {
            const active = quranFilters[key];
            return (
              <button
                key={key}
                type="button"
                aria-pressed={active}
                onClick={() => setQuranFilters((f) => ({ ...f, [key]: !f[key] }))}
                style={{
                  padding: '0.3125rem 0.75rem', borderRadius: '9999px',
                  border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  backgroundColor: active ? 'var(--color-primary)' : 'var(--color-surface)',
                  color: active ? 'var(--color-on-primary, #fff)' : 'var(--color-text-secondary)',
                  fontSize: '0.75rem', fontWeight: active ? 700 : 500, cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </button>
            );
          })}
          {quranActiveFilterCount > 0 && (
            <button
              type="button"
              onClick={() => setQuranFilters({ status: '', behind: false, noGoal: false })}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-danger)',
                padding: '0.125rem 0.25rem', textDecoration: 'underline',
              }}
            >
              {t('student.clearFilters', 'Clear All')}
            </button>
          )}
        </div>
      )}

      {/* Students Table/Cards */}
      {isMobile ? (
        // Mobile Card View
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          direction: isRTL ? 'rtl' : 'ltr'
        }}>
          {/* Loading State for Mobile */}
          {loading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem'
            }}>
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} style={{
                  backgroundColor: 'var(--color-surface)',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  animation: 'pulse 1.5s ease-in-out infinite'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--color-border)'
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ width: '60%', height: '16px', backgroundColor: 'var(--color-border)', borderRadius: '4px', marginBottom: '8px' }} />
                      <div style={{ width: '40%', height: '12px', backgroundColor: 'var(--color-border)', borderRadius: '4px' }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : visibleStudents.length === 0 ? (
            <div style={{
              backgroundColor: 'var(--color-surface)',
              borderRadius: '0.5rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              {isClinicCenter ? (
                <EmptyStates.NoClients
                  onAction={() => isHairTransplant ? navigate('/transplant-crm') : setShowModal(true)}
                />
              ) : debouncedSearch ? (
                <EmptyStates.NoSearchResults
                  query={debouncedSearch}
                  onClear={() => setSearch('')}
                />
              ) : (
                <EmptyStates.NoStudents onAction={() => setShowModal(true)} />
              )}
            </div>
          ) : (
            visibleStudents.map((student) => (
              <div
                key={student.id}
                data-testid="client-card"
                style={{
                  backgroundColor: 'var(--color-surface)',
                  borderRadius: '0.75rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
                  overflow: 'hidden',
                  border: '1px solid var(--color-border)',
                  position: 'relative'
                }}
              >
                {/* Card Header with Name and Menu */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.875rem 1rem',
                  borderBottom: '1px solid var(--color-border)'
                }}>
                  {canManageStatus && (
                    <label
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 44, minHeight: 44, marginInlineStart: '-0.5rem', flexShrink: 0, cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(student.id)}
                        onChange={() => setSelectedIds(prev => {
                          const next = new Set(prev);
                          if (next.has(student.id)) next.delete(student.id); else next.add(student.id);
                          return next;
                        })}
                        aria-label={t('sport.clientStatus.selectClient', { name: studentFullName(student, '?') })}
                        data-testid="sport-status-select"
                        style={{ width: 20, height: 20, cursor: 'pointer' }}
                      />
                    </label>
                  )}
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0, cursor: 'pointer' }}
                    onClick={() => navigate(studentDetailPath(student.id))}
                  >
                    {(() => {
                      const fullName = studentFullName(student, '?');
                      const palette = avatarColorFromName(fullName);
                      return (
                        <div style={{
                          width: '38px',
                          height: '38px',
                          borderRadius: '50%',
                          backgroundColor: palette.bg,
                          border: `1px solid ${palette.border}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: palette.fg,
                          fontWeight: '600',
                          fontSize: '0.8125rem',
                          flexShrink: 0
                        }}>
                          {studentInitials(student, fullName.charAt(0))}
                        </div>
                      );
                    })()}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{
                        margin: 0,
                        fontSize: '0.9375rem',
                        fontWeight: '600',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: 'var(--color-text-primary)'
                      }}>
                        {studentFullName(student, '?')}
                      </h3>
                      {sportInactiveBadge(student)}
                    </div>
                  </div>

                  {/* Kebab Menu */}
                  <button
                    type="button"
                    aria-label={t('common.actions', { defaultValue: 'Actions' })}
                    title={t('common.actions', { defaultValue: 'Actions' })}
                    ref={(el) => menuButtonRefs.current[student.id] = el}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (openMenuId === student.id) {
                        setOpenMenuId(null);
                      } else {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setMenuPosition({
                          top: rect.bottom + window.scrollY + 4,
                          left: isRTL ? rect.left + window.scrollX : 'auto',
                          right: isRTL ? 'auto' : window.innerWidth - rect.right - window.scrollX
                        });
                        setOpenMenuId(student.id);
                      }
                    }}
                    style={{
                      padding: '0.5rem',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '0.375rem',
                      color: 'var(--color-text-tertiary)',
                      flexShrink: 0
                    }}
                  >
                    <MoreVertical size={18} />
                  </button>
                </div>

                {/* Card Body with Contact Info */}
                <div style={{
                  padding: '0.75rem 1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  {/* Status badges — all center types */}
                  {(() => {
                    if (isHairTransplant) {
                      const status = student.status || 'profile_saved';
                      const transplantCfg = {
                        profile_saved: { bg: 'var(--color-info-light, #EFF6FF)', color: 'var(--color-info-dark, #1D4ED8)', label: t('transplantCrm.statuses.profile_saved') },
                        quote_sent: { bg: 'var(--color-warning-light, #FFF7ED)', color: 'var(--color-warning-dark, #C2410C)', label: t('transplantCrm.statuses.quote_sent') },
                        client_approved: { bg: 'var(--color-success-light, #F0FDF4)', color: 'var(--color-success-dark, #15803D)', label: t('transplantCrm.statuses.client_approved') },
                        surgery_scheduled: { bg: 'var(--color-info-light, #F0F9FF)', color: 'var(--color-info-dark, #0369A1)', label: t('transplantCrm.statuses.surgery_scheduled') },
                        cancelled: { bg: 'var(--color-danger-light, #FEF2F2)', color: 'var(--color-danger-dark, #B91C1C)', label: t('transplantCrm.statuses.cancelled') }
                      };
                      const c = transplantCfg[status] || transplantCfg.profile_saved;
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span style={{ padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.6875rem', fontWeight: '600', backgroundColor: c.bg, color: c.color }}>{c.label}</span>
                          {student.treatmentPlan?.technique && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', fontWeight: '500' }}>{student.treatmentPlan.technique}</span>
                          )}
                        </div>
                      );
                    }
                    if (isTherapyCenter) {
                      const status = student.status || 'active';
                      const therapyCfg = { active: { bg: 'var(--color-success-light, #dcfce7)', color: 'var(--color-success-dark, #166534)', label: t('common.active') }, on_hold: { bg: 'var(--color-warning-light, #fffbeb)', color: 'var(--color-warning-dark, #854d0e)', label: t('common.onHold') }, discharged: { bg: 'var(--color-surface-alt, #f3f4f6)', color: 'var(--color-text-secondary, #4b5563)', label: t('common.discharged') }, waitlist: { bg: 'var(--color-info-light, #dbeafe)', color: 'var(--color-info-dark, #1e40af)', label: t('common.waitlist') } };
                      const c = therapyCfg[status] || therapyCfg.active;
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span style={{ padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.6875rem', fontWeight: '600', backgroundColor: c.bg, color: c.color }}>{c.label}</span>
                          {surveyDataMap[student.id] && surveyDataMap[student.id].total > 0 && (
                            <span style={{ fontSize: '0.75rem', color: surveyDataMap[student.id].pending > 0 ? 'var(--warning-500)' : 'var(--success-500)', fontWeight: '600' }}>
                              {surveyDataMap[student.id].completed}/{surveyDataMap[student.id].total} {t('students.surveys')}
                            </span>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Quran (Hifz) goal progress — % of goal + on-track/behind */}
                  {isQuran && quranStatusMap[student.id] !== undefined && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <BookOpen size={14} color="var(--color-text-tertiary)" style={{ flexShrink: 0 }} />
                      <GoalProgressBadge
                        percent={quranGoalPercent(quranStatusMap[student.id])}
                        state={quranPlanState(quranStatusMap[student.id])}
                      />
                    </div>
                  )}

                  {/* Phone + WhatsApp */}
                  {student.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Phone size={14} color="var(--color-text-tertiary)" style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', flex: 1, direction: 'ltr', unicodeBidi: 'embed' }}>
                        {student.phone}
                      </span>
                      <WhatsAppButton phone={student.phone} variant="pill" />
                    </div>
                  )}

                  {/* Email */}
                  {student.email && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Mail size={14} color="var(--color-text-tertiary)" style={{ flexShrink: 0 }} />
                      <span style={{
                        fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>
                        {student.email}
                      </span>
                    </div>
                  )}

                  {/* Parent Phone (learning only) */}
                  {!isClinicCenter && student.parentPhone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <UserCircle size={14} color="var(--color-text-tertiary)" style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                        {t('students.parentPhone')}: {student.parentPhone}
                      </span>
                    </div>
                  )}

                  {/* Last session (therapy) */}
                  {isTherapyCenter && student.lastMeetingDate && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <CalendarIcon size={14} color="var(--color-text-tertiary)" style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                        {t('students.lastSession')}: {formatDateOnly(student.lastMeetingDate)}
                      </span>
                    </div>
                  )}

                  {/* No contact info */}
                  {!student.phone && !student.email && !student.parentPhone && (
                    <div style={{
                      fontSize: '0.8125rem', color: 'var(--color-text-tertiary)',
                      fontStyle: 'italic', textAlign: 'center', padding: '0.375rem'
                    }}>
                      {t('students.noContactInfo')}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          {/* Mobile Pagination */}
          {!loading && pagination.totalPages > 1 && (
            <div style={{
              backgroundColor: 'var(--color-surface)',
              borderRadius: '0.75rem',
              border: '1px solid var(--color-border)',
              padding: '0 0.75rem',
              marginTop: '0.25rem'
            }}>
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalItems}
                pageSize={pagination.pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                isRTL={isRTL}
                t={t}
              />
            </div>
          )}
        </div>
      ) : (
        // Desktop/Tablet Table View - Enterprise Grade
        <Table
          columns={(() => {
            // Filter out hideOnTablet columns when on tablet
            const filterCols = (cols) => (isTablet ? cols.filter(c => !c.hideOnTablet) : cols)
              .filter(c => !(isSport && c.key === 'grade'));
            // Responsive width helper — use smaller mins on tablet
            const rw = (desktopMin, fr) => isTablet ? `minmax(${Math.round(desktopMin * 0.7)}px, ${fr})` : `minmax(${desktopMin}px, ${fr})`;

            // Shared name column renderer with deterministic avatar color.
            const avatarSize = density === 'compact' ? 28 : 34;
            const nameColumn = () => ({
              key: 'name',
              label: t('common.name'),
              width: rw(180, '2fr'),
              sortable: true,
              render: (row) => {
                const fullName = studentFullName(row, '?');
                const palette = avatarColorFromName(fullName);
                const initials = studentInitials(row, '');
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: avatarSize, height: avatarSize, borderRadius: '50%',
                      backgroundColor: palette.bg,
                      border: `1px solid ${palette.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: palette.fg, fontWeight: '600',
                      fontSize: density === 'compact' ? '0.6875rem' : '0.8125rem',
                      flexShrink: 0,
                    }}>
                      {initials || (fullName.charAt(0))}
                    </div>
                    <span style={{ color: 'var(--color-text-primary)', fontWeight: '500' }}>
                      {fullName}
                    </span>
                    {sportInactiveBadge(row)}
                  </div>
                );
              }
            });

            // Shared phone column renderer
            const phoneColumn = () => ({
              key: 'phone',
              label: t('common.phone'),
              width: rw(140, '1.2fr'),
              sortable: true,
              render: (row) => row.phone ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', direction: 'ltr', unicodeBidi: 'embed' }}>
                  <span style={{ fontSize: '0.8125rem' }}>{row.phone}</span>
                  <WhatsAppButton phone={row.phone} size={isTablet ? 'sm' : 'md'} />
                </div>
              ) : '\u2014'
            });

            // Status badge renderer
            const statusBadge = (status, config) => {
              const cfg = config[status] || config[Object.keys(config)[0]];
              return (
                <span style={{
                  display: 'inline-flex', padding: '0.1875rem 0.5rem', borderRadius: '9999px',
                  fontSize: '0.6875rem', fontWeight: '600', backgroundColor: cfg.bg, color: cfg.color,
                  whiteSpace: 'nowrap'
                }}>
                  {cfg.label}
                </span>
              );
            };

            if (isHairTransplant) {
              const transplantStatuses = {
                profile_saved: { bg: 'var(--color-info-light, #EFF6FF)', color: 'var(--color-info-dark, #1D4ED8)', label: t('transplantCrm.statuses.profile_saved') },
                quote_sent: { bg: 'var(--color-warning-light, #FFF7ED)', color: 'var(--color-warning-dark, #C2410C)', label: t('transplantCrm.statuses.quote_sent') },
                client_approved: { bg: 'var(--color-success-light, #F0FDF4)', color: 'var(--color-success-dark, #15803D)', label: t('transplantCrm.statuses.client_approved') },
                surgery_scheduled: { bg: 'var(--color-info-light, #F0F9FF)', color: 'var(--color-info-dark, #0369A1)', label: t('transplantCrm.statuses.surgery_scheduled') },
                cancelled: { bg: 'var(--color-danger-light, #FEF2F2)', color: 'var(--color-danger-dark, #B91C1C)', label: t('transplantCrm.statuses.cancelled') }
              };
              return filterCols([
                nameColumn(),
                {
                  key: 'status',
                  label: t('common.status'),
                  width: rw(110, '1fr'),
                  render: (row) => statusBadge(row.status || 'profile_saved', transplantStatuses)
                },
                phoneColumn(),
                {
                  key: 'technique',
                  label: t('transplantCrm.technique'),
                  width: rw(90, '0.8fr'),
                  hideOnTablet: true,
                  render: (row) => (
                    <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                      {row.treatmentPlan?.technique || '\u2014'}
                    </span>
                  )
                },
                {
                  key: 'createdAt',
                  label: t('common.date'),
                  width: rw(100, '0.8fr'),
                  hideOnTablet: true,
                  render: (row) => {
                    if (!row.createdAt) return '\u2014';
                    try {
                      const d = row.createdAt?.toDate ? row.createdAt.toDate() : new Date(row.createdAt);
                      return <span style={{ fontSize: '0.8125rem' }}>{formatDateOnly(d)}</span>;
                    } catch { return '\u2014'; }
                  }
                }
              ]);
            }

            if (isTherapyCenter) {
              const therapyStatuses = {
                active: { bg: 'var(--color-success-light, #dcfce7)', color: 'var(--color-success-dark, #166534)', label: t('common.active') },
                on_hold: { bg: 'var(--color-warning-light, #fffbeb)', color: 'var(--color-warning-dark, #854d0e)', label: t('common.onHold') },
                discharged: { bg: 'var(--color-surface-alt, #f3f4f6)', color: 'var(--color-text-secondary, #4b5563)', label: t('common.discharged') },
                waitlist: { bg: 'var(--color-info-light, #dbeafe)', color: 'var(--color-info-dark, #1e40af)', label: t('common.waitlist') }
              };
              return filterCols([
                nameColumn(),
                {
                  key: 'status',
                  label: t('common.status'),
                  width: rw(90, '0.8fr'),
                  render: (row) => statusBadge(row.status || 'active', therapyStatuses)
                },
                phoneColumn(),
                {
                  key: 'surveys',
                  label: t('students.surveys'),
                  width: rw(120, '1fr'),
                  render: (row) => {
                    const sv = surveyDataMap[row.id];
                    if (!sv || sv.total === 0) return <span style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8rem' }}>{'\u2014'}</span>;
                    const pct = sv.total > 0 ? (sv.completed / sv.total) * 100 : 0;
                    const color = pct === 100 ? 'var(--success-500)' : sv.pending > 0 ? 'var(--warning-500)' : 'var(--color-text-tertiary)';
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {!isTablet && (
                          <div style={{ flex: 1, maxWidth: '60px', height: '5px', borderRadius: '3px', backgroundColor: 'var(--color-border)', overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', borderRadius: '3px', backgroundColor: color, transition: 'width 0.3s ease' }} />
                          </div>
                        )}
                        <span style={{ fontSize: '0.8rem', fontWeight: '600', color }}>{sv.completed}/{sv.total}</span>
                      </div>
                    );
                  }
                },
                {
                  key: 'lastSession',
                  label: t('students.lastSession'),
                  width: rw(110, '1fr'),
                  hideOnTablet: true,
                  render: (row) => {
                    if (!row.lastMeetingDate) return <span style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8rem' }}>{'\u2014'}</span>;
                    try {
                      const d = new Date(row.lastMeetingDate);
                      return <span style={{ fontSize: '0.8rem' }}>{formatDateOnly(d)}</span>;
                    } catch { return '\u2014'; }
                  }
                }
              ]);
            }

            // Learning center \u2014 group + grade are higher-signal than email for
            // education admins doing roster scans. Email moved to detail page.
            return filterCols([
              nameColumn(),
              {
                key: 'group',
                label: t('students.group'),
                width: rw(130, '1.2fr'),
                render: (row) => {
                  const name = resolveStudentGroup(row);
                  if (!name) {
                    return <span style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8rem' }}>{'\u2014'}</span>;
                  }
                  return (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.125rem 0.5rem',
                      borderRadius: '9999px',
                      backgroundColor: 'rgba(53, 87, 96, 0.08)',
                      color: 'var(--color-primary-dark)',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      <Layers size={11} aria-hidden="true" />
                      {name}
                    </span>
                  );
                }
              },
              {
                key: 'grade',
                label: t('students.grade'),
                width: rw(70, '0.6fr'),
                hideOnTablet: false,
                render: (row) => {
                  const g = row.grade;
                  if (!g && g !== 0) {
                    return <span style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8rem' }}>{'\u2014'}</span>;
                  }
                  return (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 26,
                      height: 22,
                      padding: '0 0.4375rem',
                      borderRadius: '0.375rem',
                      backgroundColor: 'rgba(245, 158, 11, 0.10)',
                      color: 'var(--warning-700)',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      lineHeight: 1,
                    }}>{`G${g}`}</span>
                  );
                }
              },
              ...(isQuran ? [{
                key: 'quranGoal',
                label: t('quran.progress', 'Memorization'),
                width: rw(150, '1.4fr'),
                render: (row) => {
                  const st = quranStatusMap[row.id];
                  if (st === undefined) return <span style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8rem' }}>{'—'}</span>;
                  return <GoalProgressBadge percent={quranGoalPercent(st)} state={quranPlanState(st)} />;
                }
              }] : []),
              phoneColumn(),
              {
                key: 'parentPhone',
                label: t('students.parentPhone'),
                width: rw(130, '1fr'),
                hideOnTablet: true,
                render: (row) => {
                  const v = row.parentPhone || row.guardianMobile;
                  if (!v) return <span style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8rem' }}>{'\u2014'}</span>;
                  return <span style={{ direction: 'ltr', unicodeBidi: 'embed' }}>{v}</span>;
                }
              }
            ]);
          })()}
          data={visibleStudents}
          loading={loading}
          onRowClick={(row) => navigate(studentDetailPath(row.id))}
          actions={[
            {
              key: 'view',
              label: t('studentDetail.title'),
              icon: Eye,
              onClick: (row) => navigate(studentDetailPath(row.id))
            },
            ...(isHairTransplant ? [{
              key: 'schedule',
              label: t('scheduling.scheduleTreatment'),
              icon: Scissors,
              onClick: (row) => setScheduleClient({ id: row.id, fullName: studentFullName(row) })
            }] : []),
            {
              key: 'edit',
              label: t('common.edit'),
              icon: Edit2,
              onClick: (row) => handleEditClick(row.id)
            },
            {
              key: 'delete',
              label: t('common.delete'),
              icon: Trash2,
              variant: 'danger',
              onClick: (row) => handleDeleteClick(row.id, studentFullName(row))
            }
          ]}
          pagination={{
            currentPage: pagination.currentPage,
            totalPages: pagination.totalPages,
            totalItems: pagination.totalItems,
            pageSize: pagination.pageSize,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange
          }}
          sorting={{
            column: sorting.column,
            direction: sorting.direction,
            onSort: handleSort
          }}
          selection={{
            enabled: true,
            selectedIds,
            onSelectionChange: setSelectedIds,
            bulkActions: [
              ...(canManageStatus ? [
                { key: 'markInactive', label: t('sport.clientStatus.bulkMarkInactive'), icon: PauseCircle },
                { key: 'markActive', label: t('sport.clientStatus.bulkMarkActive'), icon: UserCheck },
              ] : []),
              { key: 'export', label: t('common.export'), icon: Download },
              { key: 'delete', label: t('common.delete'), icon: Trash2, variant: 'danger' }
            ],
            onBulkAction: handleBulkAction
          }}
          emptyState={
            debouncedSearch
              ? {
                  icon: 'search',
                  title: t('students.noStudents'),
                  description: `${t('students.noResults')} "${debouncedSearch}"`,
                  action: { label: t('students.clearSearch'), onClick: () => setSearch('') }
                }
              : {
                  icon: 'users',
                  title: t('students.noStudentsYet'),
                  description: t('students.getStarted'),
                  action: { label: t('students.addStudent'), onClick: () => isHairTransplant ? navigate('/transplant-crm') : setShowModal(true) }
                }
          }
          isRTL={isRTL}
          rowKey="id"
          t={t}
        />
      )}

      {/* Add Student Modal - Enterprise Grade */}
      <EnterpriseModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setValidationErrors({});
        }}
        size="xl"
        title={t('students.addStudent')}
        titleIcon={<UserPlus size={20} />}
        subtitle={isClinicCenter
          ? t('students.addClientSubtitle', 'Just the essentials — name, contact, and date of birth. You can fill in the rest later.')
          : t('students.addStudentSubtitle', 'Required fields are marked with an asterisk. Optional details can be added below.')}
        progress={formProgress}
        mobileBottomSheet
        isSubmitting={isSubmitting}
        footer={
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setShowModal(false);
                setValidationErrors({});
              }}
              style={{
                backgroundColor: 'var(--color-border)',
                color: 'var(--color-text-primary)',
                padding: '0.625rem 1.5rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                fontSize: '0.875rem'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--color-border-dark)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--color-border)'}
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleAddStudent}
              disabled={isSubmitting}
              style={{
                backgroundColor: isSubmitting ? 'var(--color-border)' : 'var(--color-primary)',
                border: 'none',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                color: !isSubmitting ? 'var(--color-on-primary, #fff)' : 'white',
                padding: '0.625rem 2rem',
                borderRadius: '0.5rem',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                opacity: isSubmitting ? 0.7 : 1
              }}
              onMouseEnter={(e) => !isSubmitting && (e.target.style.opacity = '0.9')}
              onMouseLeave={(e) => !isSubmitting && (e.target.style.opacity = '1')}
            >
              {isSubmitting && (
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
              )}
              {isSubmitting ? t('common.saving') : t('students.addStudent')}
            </button>
          </div>
        }
      >

            {/* Section 0: Group — centers build their groups first and enrol
                every new student into one as they add them. */}
            {!isClinicCenter && (
              <div style={{ marginBottom: '1.5rem', padding: isMobile ? '0.75rem' : '1rem', backgroundColor: 'var(--color-surface-alt)', borderRadius: '0.375rem' }}>
                <GroupPicker value={newStudentGroupId} onChange={setNewStudentGroupId} disabled={isSubmitting} hint={t('clientGroup.joinsNow')} />
              </div>
            )}

            {/* Section A: Identification — for clinic centers (therapy / hair-
                transplant) this section sits behind the optional disclosure so
                intake admins can save with just the essentials. The
                autoGenerateId toggle is force-enabled on modal open for those
                centers, which keeps validation happy. */}
            {(!isClinicCenter || showOptionalSections) && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: 'var(--color-text-secondary)',
                marginBottom: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('students.identification')}
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: '1rem',
                padding: isMobile ? '0.75rem' : '1rem',
                backgroundColor: 'var(--color-surface-alt)',
                borderRadius: '0.375rem'
              }}>
                <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--color-text-primary)',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('students.nationalId')}
                  </label>
                  <input
                    type="text"
                    id="student-nationalId"
                    aria-label={t('students.nationalId')}
                    placeholder={formData.autoGenerateId ? t('students.autoGenerated') : t('students.enterNationalId')}
                    value={formData.nationalId}
                    onChange={(e) => setFormData({ ...formData, nationalId: e.target.value })}
                    disabled={formData.autoGenerateId}
                    className="input"
                    style={{
                      direction: isRTL ? 'rtl' : 'ltr',
                      backgroundColor: formData.autoGenerateId ? 'var(--color-border)' : 'var(--color-surface, white)',
                      borderColor: validationErrors.nationalId ? '#ef4444' : 'var(--color-border)'
                    }}
                  />
                  {validationErrors.nationalId && (
                    <p style={{ color: 'var(--danger-500)', fontSize: '0.75rem', marginTop: '0.25rem', textAlign: isRTL ? 'right' : 'left' }}>
                      {validationErrors.nationalId}
                    </p>
                  )}
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginTop: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                    flexDirection: isRTL ? 'row-reverse' : 'row',
                    justifyContent: isRTL ? 'flex-end' : 'flex-start'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.autoGenerateId}
                      onChange={(e) => {
                        setFormData({ ...formData, autoGenerateId: e.target.checked, nationalId: e.target.checked ? '' : formData.nationalId });
                        if (e.target.checked) {
                          setValidationErrors(prev => { const next = { ...prev }; delete next.nationalId; return next; });
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>{t('students.autoGenerateId')}</span>
                  </label>
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--color-text-primary)',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('students.gender')}
                  </label>
                  <select
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    className="input"
                    style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                  >
                    <option value="">{t('students.selectGender')}</option>
                    <option value="male">{t('students.male')}</option>
                    <option value="female">{t('students.female')}</option>
                  </select>
                </div>
              </div>
            </div>
            )}

            {/* Section B: Personal Information */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: 'var(--color-text-secondary)',
                marginBottom: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('students.personalInfo')}
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: '1rem',
                padding: isMobile ? '0.75rem' : '1rem',
                backgroundColor: 'var(--color-surface-alt)',
                borderRadius: '0.375rem'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--color-text-primary)',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('students.firstName')} *
                  </label>
                  <input
                    type="text"
                    placeholder={t('students.firstName')}
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="input"
                    style={{
                      direction: isRTL ? 'rtl' : 'ltr',
                      borderColor: validationErrors.firstName ? '#ef4444' : 'var(--color-border)'
                    }}
                    required
                  />
                  {validationErrors.firstName && (
                    <p style={{ color: 'var(--danger-500)', fontSize: '0.75rem', marginTop: '0.25rem', textAlign: isRTL ? 'right' : 'left' }}>
                      {validationErrors.firstName}
                    </p>
                  )}
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--color-text-primary)',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('students.fatherName')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('students.fatherName')}
                    value={formData.middleName}
                    onChange={(e) => setFormData({ ...formData, middleName: e.target.value })}
                    className="input"
                    style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--color-text-primary)',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('students.lastName')} *
                  </label>
                  <input
                    type="text"
                    placeholder={t('students.lastName')}
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="input"
                    style={{
                      direction: isRTL ? 'rtl' : 'ltr',
                      borderColor: validationErrors.lastName ? '#ef4444' : 'var(--color-border)'
                    }}
                    required
                  />
                  {validationErrors.lastName && (
                    <p style={{ color: 'var(--danger-500)', fontSize: '0.75rem', marginTop: '0.25rem', textAlign: isRTL ? 'right' : 'left' }}>
                      {validationErrors.lastName}
                    </p>
                  )}
                </div>
                <div>
                  <label style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--color-text-primary)',
                    direction: isRTL ? 'rtl' : 'ltr',
                    gap: '0.5rem',
                  }}>
                    <span>{t('students.dateOfBirth')}</span>
                    {computedAge !== null && (
                      <span style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: 'var(--color-primary)',
                        backgroundColor: 'rgba(53, 87, 96, 0.08)',
                        padding: '0.0625rem 0.5rem',
                        borderRadius: '9999px',
                      }}>
                        {t('students.ageYears', { count: computedAge, defaultValue: '{{count}} yrs' })}
                      </span>
                    )}
                  </label>
                  <input
                    type="date"
                    value={formData.dateOfBirth}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                    className="input"
                    style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                  />
                </div>
              </div>
            </div>

            {/* Section C: Contact Details */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: 'var(--color-text-secondary)',
                marginBottom: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('students.contactDetails')}
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '1rem',
                padding: isMobile ? '0.75rem' : '1rem',
                backgroundColor: 'var(--color-surface-alt)',
                borderRadius: '0.375rem'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--color-text-primary)',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('common.phone')}
                  </label>
                  <input
                    type="tel"
                    placeholder="05XXXXXXXX"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/[\s-]/g, '') })}
                    className="input"
                    inputMode="numeric"
                    autoComplete="tel"
                    maxLength={10}
                    style={{
                      direction: 'ltr',
                      textAlign: isRTL ? 'right' : 'left',
                      borderColor: validationErrors.phone ? '#ef4444' : 'var(--color-border)'
                    }}
                  />
                  {validationErrors.phone ? (
                    <p style={{ color: 'var(--danger-500)', fontSize: '0.75rem', marginTop: '0.25rem', textAlign: isRTL ? 'right' : 'left' }}>
                      {validationErrors.phone}
                    </p>
                  ) : (
                    <p style={{ color: 'var(--color-text-tertiary)', fontSize: '0.7rem', marginTop: '0.25rem', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('students.phoneHelp', '10 digits, starts with 05')}
                    </p>
                  )}
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--color-text-primary)',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('common.email')}
                  </label>
                  <input
                    type="email"
                    placeholder="example@domain.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input"
                    style={{ direction: 'ltr', textAlign: isRTL ? 'right' : 'left' }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--color-text-primary)',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('students.city')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('students.city')}
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="input"
                    style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--color-text-primary)',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('students.address')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('students.address')}
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="input"
                    style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                  />
                </div>
              </div>
            </div>

            {/* Toggle: progressive disclosure for guardian + checklists.
                The required-fields-first pattern keeps the cognitive load low
                for fast roster entry; power users one-click to reveal the rest. */}
            {!showOptionalSections && (
              <div style={{ marginBottom: '1.25rem' }}>
                <button
                  type="button"
                  onClick={() => setShowOptionalSections(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0.5rem 0.875rem',
                    backgroundColor: 'var(--color-surface-alt)',
                    border: '1px dashed var(--color-border)',
                    borderRadius: '0.5rem',
                    color: 'var(--color-primary-dark)',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    width: '100%',
                    justifyContent: 'center',
                    transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(53,87,96,0.06)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-surface-alt)'; }}
                >
                  <ChevronDown size={15} />
                  {t('students.showMoreDetails', 'Add guardian info & checklists')}
                </button>
              </div>
            )}

            {/* Section D: Guardian Information */}
            {showOptionalSections && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: 'var(--color-text-secondary)',
                marginBottom: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('students.guardianInfo')}
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 2fr',
                gap: '1rem',
                padding: isMobile ? '0.75rem' : '1rem',
                backgroundColor: 'var(--color-surface-alt)',
                borderRadius: '0.375rem'
              }}>
                <div style={{ gridColumn: isMobile ? 'span 1' : 'auto' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--color-text-primary)',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('students.guardianName')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('students.guardianName')}
                    value={formData.guardianName}
                    onChange={(e) => setFormData({ ...formData, guardianName: e.target.value })}
                    className="input"
                    style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--color-text-primary)',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('students.relationship')}
                  </label>
                  <select
                    value={formData.guardianRelationship}
                    onChange={(e) => setFormData({ ...formData, guardianRelationship: e.target.value })}
                    className="input"
                    style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                  >
                    <option value="">{t('students.selectRelationship')}</option>
                    <option value="father">{t('students.father')}</option>
                    <option value="mother">{t('students.mother')}</option>
                    <option value="uncle">{t('students.uncle')}</option>
                    <option value="aunt">{t('students.aunt')}</option>
                    <option value="other">{t('students.other')}</option>
                  </select>
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--color-text-primary)',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('students.guardianMobile')}
                  </label>
                  <input
                    type="tel"
                    placeholder="05XXXXXXXX"
                    value={formData.guardianMobile}
                    onChange={(e) => setFormData({ ...formData, guardianMobile: e.target.value })}
                    className="input"
                    style={{
                      direction: 'ltr',
                      textAlign: isRTL ? 'right' : 'left',
                      borderColor: validationErrors.guardianMobile ? '#ef4444' : 'var(--color-border)'
                    }}
                  />
                  {validationErrors.guardianMobile && (
                    <p style={{ color: 'var(--danger-500)', fontSize: '0.75rem', marginTop: '0.25rem', textAlign: isRTL ? 'right' : 'left' }}>
                      {validationErrors.guardianMobile}
                    </p>
                  )}
                </div>
                <div style={{ gridColumn: isMobile ? 'span 1' : 'span 3' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--color-text-primary)',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('students.guardianId')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('students.guardianId')}
                    value={formData.guardianId}
                    onChange={(e) => setFormData({ ...formData, guardianId: e.target.value })}
                    className="input"
                    style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                  />
                </div>
              </div>
            </div>
            )}

            {/* Therapy Info Section */}
            {isTherapyCenter && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{
                  fontSize: '0.875rem', fontWeight: '600', color: 'var(--color-text-secondary)',
                  marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('students.therapyInfo')}
                </h3>
                <div style={{
                  display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '1rem',
                  padding: isMobile ? '0.75rem' : '1rem', backgroundColor: 'var(--color-surface-alt)', borderRadius: '0.375rem'
                }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--color-text-primary)', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('students.referralSource')}
                    </label>
                    <input type="text" placeholder={t('students.referralSource')} value={formData.referralSource || ''} onChange={(e) => setFormData({ ...formData, referralSource: e.target.value })} className="input" style={{ direction: isRTL ? 'rtl' : 'ltr' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--color-text-primary)', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('students.initialStatus')}
                    </label>
                    <select value={formData.initialStatus || 'active'} onChange={(e) => setFormData({ ...formData, initialStatus: e.target.value })} className="input" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                      <option value="active">{t('common.active')}</option>
                      <option value="waitlist">{t('common.waitlist')}</option>
                    </select>
                  </div>
                  <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                    <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--color-text-primary)', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('students.intakeNotes')}
                    </label>
                    <textarea placeholder={t('students.intakeNotes')} value={formData.intakeNotes || ''} onChange={(e) => setFormData({ ...formData, intakeNotes: e.target.value })} className="input" rows={3} style={{ direction: isRTL ? 'rtl' : 'ltr', resize: 'vertical', fontFamily: 'inherit' }} />
                  </div>
                </div>
              </div>
            )}

            {/* Checklists Section */}
            {showOptionalSections && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: 'var(--color-text-secondary)',
                marginBottom: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('settings.checklists')}
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                maxHeight: '150px',
                overflowY: 'auto',
                border: '1px solid var(--color-border)',
                borderRadius: '0.375rem',
                padding: '0.75rem',
                backgroundColor: 'var(--color-surface-alt)'
              }}>
                {checklists.length > 0 ? (
                  checklists.map((checklist) => (
                    <label key={checklist.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                      color: 'var(--color-text-primary)',
                      flexDirection: isRTL ? 'row-reverse' : 'row',
                      justifyContent: isRTL ? 'flex-end' : 'flex-start'
                    }}>
                      <input
                        type="checkbox"
                        checked={selectedChecklists.includes(checklist.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedChecklists([...selectedChecklists, checklist.id]);
                          } else {
                            setSelectedChecklists(selectedChecklists.filter(id => id !== checklist.id));
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>{checklist.title} ({t('students.checklistItemsCount', { count: checklist.items?.length || 0 })})</span>
                    </label>
                  ))
                ) : (
                  <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-text-tertiary)', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('groups.noChecklistsAvailable')}
                  </p>
                )}
              </div>
            </div>
            )}

            {/* Footer Actions */}
      </EnterpriseModal>

      {/* Bottom Sheet Menu (Mobile) */}
      {openMenuId && isMobile && ReactDOM.createPortal(
        <>
          {/* Backdrop */}
          <div
            onClick={() => setOpenMenuId(null)}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              zIndex: 9998,
              animation: 'fadeIn 0.15s ease'
            }}
          />
          {/* Bottom Sheet */}
          <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'var(--color-surface)',
            borderRadius: '1rem 1rem 0 0',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.12)',
            zIndex: 9999,
            overflow: 'hidden',
            paddingBottom: 'env(safe-area-inset-bottom, 16px)',
            animation: 'sheetSlideUp 0.2s ease'
          }}>
            {/* Handle bar */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '0.75rem 0 0.5rem'
            }}>
              <div style={{
                width: '36px',
                height: '4px',
                borderRadius: '2px',
                backgroundColor: 'var(--color-border)'
              }} />
            </div>
            {/* Client name header */}
            {(() => {
              const student = students.find(s => s.id === openMenuId);
              return student ? (
                <div style={{
                  padding: '0 1.25rem 0.75rem',
                  fontSize: '0.8125rem',
                  color: 'var(--color-text-tertiary)',
                  fontWeight: '500',
                  borderBottom: '1px solid var(--color-border)'
                }}>
                  {studentFullName(student)}
                </div>
              ) : null;
            })()}
            {/* Actions */}
            <div style={{ padding: '0.5rem 0' }}>
              <button
                onClick={() => {
                  const student = students.find(s => s.id === openMenuId);
                  if (student) navigate(studentDetailPath(student.id));
                  setOpenMenuId(null);
                }}
                style={{
                  width: '100%',
                  padding: '0.875rem 1.25rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  textAlign: isRTL ? 'right' : 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.875rem',
                  color: 'var(--color-text-primary)',
                  fontSize: '0.9375rem',
                  fontWeight: '500',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}
              >
                <Eye size={18} style={{ color: 'var(--color-text-tertiary)' }} />
                {t('common.viewDetails', { defaultValue: 'View Details' })}
              </button>
              {isHairTransplant && (
                <button
                  onClick={() => {
                    const student = students.find(s => s.id === openMenuId);
                    if (student) setScheduleClient({ id: student.id, fullName: studentFullName(student) });
                    setOpenMenuId(null);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1.25rem',
                    backgroundColor: 'transparent',
                    border: 'none',
                    textAlign: isRTL ? 'right' : 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.875rem',
                    color: 'var(--color-text-primary)',
                    fontSize: '0.9375rem',
                    fontWeight: '500',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }}
                >
                  <Scissors size={18} style={{ color: '#8b5cf6' }} />
                  {t('scheduling.scheduleTreatment', 'Schedule Treatment')}
                </button>
              )}
              <button
                onClick={() => {
                  const student = students.find(s => s.id === openMenuId);
                  if (student) handleEditClick(student.id);
                  setOpenMenuId(null);
                }}
                style={{
                  width: '100%',
                  padding: '0.875rem 1.25rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  textAlign: isRTL ? 'right' : 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.875rem',
                  color: 'var(--color-text-primary)',
                  fontSize: '0.9375rem',
                  fontWeight: '500',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}
              >
                <Edit2 size={18} style={{ color: 'var(--color-text-tertiary)' }} />
                {t('common.edit')}
              </button>
              <button
                onClick={() => {
                  const student = students.find(s => s.id === openMenuId);
                  if (student) {
                    const phone = (student.phone || student.guardianMobile || '').replace(/^0/, '972');
                    if (phone) openExternal(`https://wa.me/${phone}`);
                  }
                  setOpenMenuId(null);
                }}
                style={{
                  width: '100%',
                  padding: '0.875rem 1.25rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  textAlign: isRTL ? 'right' : 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.875rem',
                  color: '#25D366',
                  fontSize: '0.9375rem',
                  fontWeight: '500',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#25D366">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </button>
              <div style={{ height: '1px', backgroundColor: 'var(--color-border)', margin: '0.25rem 1.25rem' }} />
              <button
                onClick={() => {
                  const student = students.find(s => s.id === openMenuId);
                  if (student) handleDeleteClick(student.id, studentFullName(student));
                  setOpenMenuId(null);
                }}
                style={{
                  width: '100%',
                  padding: '0.875rem 1.25rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  textAlign: isRTL ? 'right' : 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.875rem',
                  color: 'var(--color-error)',
                  fontSize: '0.9375rem',
                  fontWeight: '500',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}
              >
                <Trash2 size={18} />
                {t('common.delete')}
              </button>
            </div>
            {/* Cancel button */}
            <div style={{ padding: '0 1rem 0.75rem' }}>
              <button
                onClick={() => setOpenMenuId(null)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: 'var(--color-surface-alt)',
                  border: 'none',
                  borderRadius: '0.75rem',
                  cursor: 'pointer',
                  fontSize: '0.9375rem',
                  fontWeight: '600',
                  color: 'var(--color-text-secondary)'
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
          <style>{`
            @keyframes sheetSlideUp {
              from { transform: translateY(100%); }
              to { transform: translateY(0); }
            }
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
          `}</style>
        </>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title={t('students.deleteStudent')}
        message={`${t('students.deleteConfirm')} (${studentToDelete?.name})`}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setStudentToDelete(null);
        }}
        isDangerous={true}
      />

      {/* Generic CSV/XLSX Import Modal (column mapping + notes fallback) */}
      <StudentImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={() => {
          setShowImportModal(false);
          setPagination(prev => ({ ...prev, currentPage: 1 }));
          setRefetchTrigger(prev => prev + 1);
          success(t('students.importSuccess'));
        }}
        selectedYear={selectedYear}
      />

      {/* Duplicate detection (scans the whole roster, archives the extras) */}
      <DuplicateDetectionModal
        isOpen={showDuplicates}
        onClose={() => setShowDuplicates(false)}
        mode="global"
        isTherapyCenter={isTherapyCenter}
        loadStudents={async () => {
          const res = await api.get('/students', { params: { limit: 1000, yearId: selectedYear } });
          return Array.isArray(res) ? res : (res?.data || []);
        }}
        onResolved={() => setRefetchTrigger(prev => prev + 1)}
      />

      {scheduleClient && (
        <Suspense fallback={null}>
          <ScheduleTreatmentModal
            isOpen={!!scheduleClient}
            onClose={() => setScheduleClient(null)}
            onSuccess={() => setScheduleClient(null)}
            prefilledClient={scheduleClient}
          />
        </Suspense>
      )}
    </div>
  );
}
